import { useEffect, useState, useCallback, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { api, API_BASE, WS_BASE, apiHref } from "@/lib/api";
import { prepareAutoPrintTab, finishAutoPrint } from "@/lib/autoPrint";
import { sortMixes } from "@/lib/mixSort";
import { usePlant } from "@/context/PlantContext";
import { useAuth } from "@/context/AuthContext";
import { isFleet, FLEET_UNITS } from "@/lib/industry";
import { speakTruckBack, autoArmOnFirstGesture } from "@/lib/voice";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { OrderQuote } from "@/components/OrderQuote";
import { PumpUnitSelect } from "@/components/PumpUnitSelect";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Truck, Plus, X, Radio, Calendar as CalIcon, Pencil, CheckCircle2, RotateCcw,
  Sparkles, Factory, ClipboardList, AlertTriangle, Play, Printer, RefreshCcw, MapPin, MoreVertical, QrCode,
  XOctagon, Timer, Coins, Copy, MessageSquare, Clock, ChevronsLeft, ChevronsRight, MessageSquareWarning, Receipt,
} from "lucide-react";
import { toast } from "sonner";
import { mixColor } from "@/lib/mixColor";
import { localToday, shiftLocalDate, formatLocalDate } from "@/lib/dates";
import AIOptimiserDialog from "@/components/AIOptimiserDialog";
import SmsComposeDialog from "@/components/SmsComposeDialog";
import JobShareQR from "@/components/JobShareQR";
import QuickEditDocketPopover from "@/components/QuickEditDocketPopover";
import PrintScheduleButton from "@/components/PrintScheduleButton";
import PriceCalcPanel from "@/components/PriceCalcPanel";
import WeatherReplyWatcher from "@/components/WeatherReplyWatcher";
import { SYNCED_EVENT } from "@/lib/offline";
const LANE_OF = {
  available: "available",
  loading: "loading",
  slumping: "loading",
  en_route: "to_site",
  on_site: "on_job",
  discharging: "on_job",
  returning: "returning",
  out_of_service: "ooos",
};

const LANE_META = {
  available: { label: "AVAILABLE", color: "#F57C00", status: "available", hint: "Drag onto a job to assign" },
  loading: { label: "LOADING", color: "#EAB308", status: "loading", hint: "Truck is being batched" },
  to_site: { label: "TO SITE", color: "#F97316", status: "en_route", hint: "En-route to customer" },
  on_job: { label: "ON JOB", color: "#22C55E", status: "on_site", hint: "On site / discharging" },
  returning: { label: "RETURNING", color: "#3B82F6", status: "returning", hint: "Heading back to yard" },
  ooos: { label: "OUT OF SERVICE", color: "#71717A", status: "out_of_service", hint: "Parked up — not in use today" },
};

const LANE_ORDER = ["available", "loading", "to_site", "on_job", "returning", "ooos"];

// Waiting-time rules — must mirror backend
const WAIT_GRACE_MIN = 45;
const WAIT_RATE = 3.0;

function computeWait(arrivedAtIso) {
  if (!arrivedAtIso) return null;
  const arrived = new Date(arrivedAtIso).getTime();
  const nowMs = Date.now();
  const minutes = (nowMs - arrived) / 60000;
  const over = minutes - WAIT_GRACE_MIN;
  if (over <= 0) return { state: "grace", minutesLeft: Math.max(0, Math.ceil(WAIT_GRACE_MIN - minutes)), charge: 0, chargeMinutes: 0 };
  const chargeable = Math.ceil(over);
  return { state: "charging", minutesLeft: 0, chargeMinutes: chargeable, charge: chargeable * WAIT_RATE };
}

// ── Truck load-spacing scheduler ─────────────────────────────────────────
// Given the job's start time (HH:MM), trucks needed, and minutes between
// loads, produce the sequence of departure times. Used both in the New Job
// dialog preview and the Command row's "next load" chip / pulse trigger.
function computeLoadSlots(startTime, trucksNeeded, spacingMin) {
  if (!startTime || !trucksNeeded || !spacingMin) return [];
  const [hh, mm] = startTime.split(":").map((n) => parseInt(n, 10));
  if (isNaN(hh) || isNaN(mm)) return [];
  return Array.from({ length: trucksNeeded }, (_, i) => {
    const total = hh * 60 + mm + i * spacingMin;
    const H = Math.floor((total / 60) % 24);
    const M = total % 60;
    return `${String(H).padStart(2, "0")}:${String(M).padStart(2, "0")}`;
  });
}

// Return { nextTime, minutesUntil, pulse, allDone } given a job and Date.now().
// - loadsIssued = number of dockets already batched (non-cancelled)
// - pulse = true when the next slot is <=5 min away or already overdue
function computeNextLoad(job, dockets, nowMs) {
  const spacing = Number(job.load_spacing_min || 0);
  const trucks = Number(job.trucks_needed || 0);
  if (!spacing || !trucks || !job.time_required) return null;
  const issued = (dockets || []).filter(
    (d) => d.job_id === job.id && d.status !== "cancelled"
  ).length;
  if (issued >= trucks) return { allDone: true };
  const slots = computeLoadSlots(job.time_required, trucks, spacing);
  const nextTime = slots[issued];
  if (!nextTime) return null;
  const [H, M] = nextTime.split(":").map((n) => parseInt(n, 10));
  const scheduled = new Date();
  scheduled.setHours(H, M, 0, 0);
  const minutesUntil = Math.round((scheduled.getTime() - nowMs) / 60000);
  return { nextTime, minutesUntil, pulse: minutesUntil <= 5, slotIndex: issued + 1, totalSlots: trucks };
}

const KpiCell = ({ icon: Icon, label, value, sub }) => (
  <div className="flex items-start gap-2.5 border-r border-industrial pr-4 last:border-r-0" data-testid={`kpi-${label.toLowerCase().replace(/\s+/g, "-")}`}>
    <div className="w-7 h-7 border border-industrial flex items-center justify-center bg-surface-2 shrink-0">
      <Icon className="w-3.5 h-3.5 text-primary-orange" strokeWidth={2} />
    </div>
    <div>
      <div className="label-tag text-[9px]">{label}</div>
      <div className="mono text-lg font-bold tracking-tight text-ink leading-none mt-1">{value}</div>
      {sub && <div className="text-[9px] text-muted-industrial mt-0.5">{sub}</div>}
    </div>
  </div>
);

/* ── Inline site-address quick edit — pencil popover on each job card ── */
function AddressQuickEdit({ job, onSaved }) {
  const [open, setOpen] = useState(false);
  const [addr, setAddr] = useState(job.site_address || "");
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) setAddr(job.site_address || ""); }, [open, job.site_address]);
  const save = async () => {
    const next = (addr || "").trim();
    if (!next) return toast.error("Address cannot be empty");
    if (next === (job.site_address || "").trim()) { setOpen(false); return; }
    setSaving(true);
    try {
      await api.patch(`/jobs/${job.id}`, { site_address: next });
      toast.success("Site address updated");
      setOpen(false);
      onSaved && onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update address");
    } finally { setSaving(false); }
  };
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={`edit-site-address-${job.id}`}
          onClick={(e) => { e.stopPropagation(); }}
          title="Quick-edit site address"
          className="shrink-0 p-0.5 -my-0.5 opacity-40 hover:opacity-100 hover:text-primary-orange transition-opacity"
        >
          <Pencil className="w-2.5 h-2.5" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[380px] p-3" onClick={(e) => e.stopPropagation()}>
        <div className="label-tag text-[9px] mb-1.5">Edit site address</div>
        <Input
          data-testid={`edit-site-address-input-${job.id}`}
          value={addr}
          onChange={(e) => setAddr(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") save(); if (e.key === "Escape") setOpen(false); }}
          placeholder="123 Example St, Suburb"
          autoFocus
        />
        <div className="text-[10px] text-muted-industrial mt-1.5">
          Auto re-geocodes the delivery km. This detaches the job from any saved site.
        </div>
        <div className="flex justify-end gap-2 mt-2.5">
          <Button variant="ghost" size="sm" onClick={() => setOpen(false)} data-testid={`edit-site-address-cancel-${job.id}`}>Cancel</Button>
          <Button size="sm" onClick={save} disabled={saving} data-testid={`edit-site-address-save-${job.id}`}>
            {saving ? "Saving…" : "Save"}
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}


/* ── Inline slump quick-edit — click the slump text on any job to override ── */
function SlumpQuickEdit({ job, onSaved }) {
  const [open, setOpen] = useState(false);
  const [val, setVal] = useState(job.slump_mm ?? 80);
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) setVal(job.slump_mm ?? 80); }, [open, job.slump_mm]);
  const save = async () => {
    const n = parseInt(val, 10);
    if (isNaN(n) || n < 0 || n > 300) return toast.error("Slump must be between 0 and 300 mm");
    if (n === (job.slump_mm ?? 80)) { setOpen(false); return; }
    setSaving(true);
    try {
      await api.patch(`/jobs/${job.id}`, { slump_mm: n });
      toast.success(`Slump set to ${n} mm`);
      setOpen(false);
      onSaved && onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update slump");
    } finally { setSaving(false); }
  };
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={`edit-slump-${job.id}`}
          onClick={(e) => { e.stopPropagation(); }}
          title="Click to override the slump for this order"
          className="mono text-[9px] text-muted-industrial mt-0.5 hover:text-primary-orange transition-colors inline-flex items-center gap-1 group"
        >
          {job.slump_mm ?? "—"}mm
          <Pencil className="w-2 h-2 opacity-0 group-hover:opacity-100 transition-opacity" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[240px] p-3" onClick={(e) => e.stopPropagation()}>
        <div className="label-tag text-[9px] mb-1.5">Override slump</div>
        <div className="flex items-center gap-2">
          <Input
            data-testid={`edit-slump-input-${job.id}`}
            type="number"
            min="0"
            max="300"
            step="10"
            value={val}
            onChange={(e) => setVal(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") save(); if (e.key === "Escape") setOpen(false); }}
            autoFocus
            className="w-full"
          />
          <span className="mono text-[10px] text-muted-industrial shrink-0">mm</span>
        </div>
        <div className="flex gap-1 mt-2">
          {[60, 80, 100, 120, 140].map((preset) => (
            <button
              key={preset}
              type="button"
              onClick={() => setVal(preset)}
              className="mono text-[10px] px-1.5 py-0.5 border border-industrial hover:border-primary-orange hover:text-primary-orange"
            >{preset}</button>
          ))}
        </div>
        <div className="flex justify-end gap-2 mt-2.5">
          <Button variant="ghost" size="sm" onClick={() => setOpen(false)} data-testid={`edit-slump-cancel-${job.id}`}>Cancel</Button>
          <Button size="sm" onClick={save} disabled={saving} data-testid={`edit-slump-save-${job.id}`}>
            {saving ? "Saving…" : "Save"}
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}



/* ── Out-of-hours window · Mon–Fri 06:00–17:00, Sat 06:00–12:00, Sun always OOH ── */
const TRADING_HOURS = { weekday: [6, 17], saturday: [6, 12] };
function isOutOfHours(dateStr, timeStr) {
  if (!dateStr || !timeStr) return false;
  const [h, m] = String(timeStr).split(":").map(Number);
  if (Number.isNaN(h)) return false;
  const mins = h * 60 + (m || 0);
  const dow = new Date(`${dateStr}T00:00:00`).getDay(); // 0 = Sun
  if (dow === 0) return true;
  const [open, close] = dow === 6 ? TRADING_HOURS.saturday : TRADING_HOURS.weekday;
  return mins < open * 60 || mins >= close * 60;
}
const findOohSurcharge = (items) =>
  (items || []).find((x) => x.active && (String(x.code).toUpperCase() === "OOH" || /out.?of.?hours/i.test(x.name || "")));

/* ── Extra Tag Picker — colored chip multi-select ─────────────────────── */
function SurchargePicker({ items, selectedIds, onChange, testid }) {
  const active = (items || []).filter((x) => x.active);
  if (active.length === 0) return <div className="text-[10px] text-muted-industrial mono uppercase tracking-widest border border-dashed border-industrial p-2">No one-off surcharges defined — add them under Surcharges</div>;
  const toggle = (id) => { const s = new Set(selectedIds || []); if (s.has(id)) s.delete(id); else s.add(id); onChange(Array.from(s)); };
  return (
    <div className="flex flex-wrap gap-1.5" data-testid={testid || "surcharge-picker"}>
      {active.map((x) => { const on = (selectedIds || []).includes(x.id); return (
        <button key={x.id} type="button" data-testid={`surcharge-chip-${x.code}`} onClick={() => toggle(x.id)} title="Flat fee · billed once on the first docket"
          className={`mono text-[10px] uppercase tracking-widest px-2 py-1 border transition-colors ${on ? "bg-primary-orange border-primary-orange text-white font-bold" : "border-industrial text-muted-industrial hover:text-ink"}`}>
          {on ? "✓ " : ""}{x.name} <span className="ml-1 opacity-80">${Math.round(x.amount)}</span>
        </button>); })}
    </div>
  );
}

function ExtraTagPicker({ tags, selectedIds, onChange, testid }) {
  if (!tags || tags.length === 0) {
    return <div className="text-[10px] text-muted-industrial mono uppercase tracking-widest border border-dashed border-industrial p-2">No extras defined — add them in Settings → Extras</div>;
  }
  const toggle = (id) => {
    const s = new Set(selectedIds || []);
    if (s.has(id)) s.delete(id); else s.add(id);
    onChange(Array.from(s));
  };
  return (
    <div className="flex flex-wrap gap-1.5" data-testid={testid || "extra-tag-picker"}>
      {tags.map((t) => {
        const on = (selectedIds || []).includes(t.id);
        return (
          <button
            key={t.id}
            type="button"
            data-testid={`extra-tag-${t.name.toLowerCase()}`}
            onClick={() => toggle(t.id)}
            className={`mono text-[10px] uppercase tracking-widest px-2 py-1 border transition-colors ${on ? "font-bold" : "opacity-60 hover:opacity-100"}`}
            style={{
              borderColor: t.color,
              color: on ? "#ffffff" : t.color,
              background: on ? t.color : "transparent",
            }}
            title={t.price_per_m3 > 0 ? `+$${t.price_per_m3.toFixed(2)}/m³` : ""}
          >
            {on ? "✓ " : ""}{t.name}
            {t.price_per_m3 > 0 && <span className="ml-1 opacity-70">+${Math.round(t.price_per_m3)}</span>}
          </button>
        );
      })}
    </div>
  );
}

/* ── ETA countdown helper (mirrors Dispatch board) ────────────────
   Returns a compact status string ("in 12m", "8m late", "NOW") for a
   HH:MM target time compared to `nowMs`. Wraps 24h so overnight shifts
   don't spike into 1440m late. */
function etaStatusC(hhmm, nowMs) {
  if (!hhmm) return { text: "", color: "text-muted-industrial", flash: false, minutes: null };
  const [h, m] = hhmm.split(":").map((x) => parseInt(x, 10));
  if (isNaN(h) || isNaN(m)) return { text: "", color: "text-muted-industrial", flash: false, minutes: null };
  const now = new Date(nowMs || Date.now());
  const target = new Date(now); target.setHours(h, m, 0, 0);
  let deltaMin = Math.round((target - now) / 60000);
  if (deltaMin < -720) deltaMin += 1440;
  if (deltaMin > 720) deltaMin -= 1440;
  const fmt = (n) => {
    if (n < 60) return `${n}m`;
    const hrs = Math.floor(n / 60);
    const mn = n % 60;
    return mn === 0 ? `${hrs}h` : `${hrs}h${mn}`;
  };
  if (deltaMin > 60) return { text: fmt(deltaMin), color: "text-muted-industrial", flash: false, minutes: deltaMin };
  if (deltaMin > 5)  return { text: fmt(deltaMin), color: "text-emerald-500", flash: false, minutes: deltaMin };
  if (deltaMin > 0)  return { text: fmt(deltaMin), color: "text-amber-500", flash: false, minutes: deltaMin };
  if (deltaMin === 0) return { text: "NOW", color: "text-primary-orange font-bold", flash: false, minutes: 0 };
  if (deltaMin > -10) return { text: `${fmt(Math.abs(deltaMin))} LATE`, color: "text-orange-500 font-bold", flash: false, minutes: deltaMin };
  return { text: `${fmt(Math.abs(deltaMin))} LATE`, color: "text-red-500 font-bold", flash: true, minutes: deltaMin };
}

/* ── Truck chip — single-click state advancer + edge actions ───────────
   State cycle (per click):
     READY  →  BATCHING  →  EN ROUTE  →  ON SITE  →  DISCHARGING  →  YARD
   READY: truck assigned but no batch yet ("START SEQUENCE" fires the batch)
   Edge actions (⋮): Cancel Load, Print Docket, Unassign, Reset wait clock
*/
function TruckChip({ truck, job, batch, docketByTruckJob, onAdvance, onUnassign, onSwap, onCancelBatch, onLoadChange, isCompleted, prediction }) {
  const [open, setOpen] = useState(false);
  const [loadDialogOpen, setLoadDialogOpen] = useState(false);
  const [loadVal, setLoadVal] = useState("");
  // v3.6o — Per-truck ETA popover on the Plant Command page (parity with
  // Dispatch board). Stores eta_to_site + eta_back_to_plant on
  // job.truck_etas via POST /jobs/{jid}/truck-eta. Auto-advance ETA
  // service keeps eta_back_to_plant self-correcting when the truck flips
  // to `returning`.
  const initialEta = (job.truck_etas && job.truck_etas[truck.id]) || {};
  const [etaOpen, setEtaOpen] = useState(false);
  const [etaToSite, setEtaToSite] = useState(initialEta.eta_to_site || "");
  const [etaBack, setEtaBack] = useState(initialEta.eta_back_to_plant || "");
  const etaAuto = !!initialEta.eta_back_auto;
  const [nowMs, setNowMs] = useState(Date.now());
  useEffect(() => {
    const fresh = (job.truck_etas && job.truck_etas[truck.id]) || {};
    setEtaToSite(fresh.eta_to_site || "");
    setEtaBack(fresh.eta_back_to_plant || "");
  }, [job.truck_etas, truck.id]);
  useEffect(() => {
    const id = setInterval(() => setNowMs(Date.now()), 30000);
    return () => clearInterval(id);
  }, []);
  // Default the load to what's still owed on the job (capped by the barrel), not the
  // barrel size — a 7 m³ truck finishing a 3.6 m³ order is carrying 3.6.
  const _cap = Number(truck.capacity_m3 || 0);
  const _owed = Math.max(0, Number(job.quantity_m3 || 0) - Number(job.delivered_m3 || 0));
  const currentLoad = (job.truck_loads && job.truck_loads[truck.id] != null)
    ? Number(job.truck_loads[truck.id])
    : (_owed > 0 ? Math.min(_cap || _owed, _owed) : _cap);
  const loadLocked = isCompleted || truck.status === "en_route" || truck.status === "on_site" || truck.status === "discharging" || truck.status === "returning";
  const openLoadDialog = (e) => {
    e && e.stopPropagation && e.stopPropagation();
    if (loadLocked) return;
    setLoadVal(String(currentLoad));
    setLoadDialogOpen(true);
  };
  const commitLoad = async () => {
    const q = parseFloat(loadVal);
    if (isNaN(q) || q <= 0) { toast.error("Load size must be greater than 0"); return; }
    if (q === currentLoad) { setLoadDialogOpen(false); return; }
    try {
      await api.post(`/jobs/${job.id}/truck-load`, { truck_id: truck.id, quantity_m3: q });
      toast.success(`${truck.number} · ${q} m³`);
      onLoadChange && onLoadChange();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update truck load");
    }
    setLoadDialogOpen(false);
  };
  const commitEta = async () => {
    try {
      await api.post(`/jobs/${job.id}/truck-eta`, {
        truck_id: truck.id,
        eta_to_site: etaToSite || "",
        eta_back_to_plant: etaBack || "",
      });
      toast.success(`${truck.number} · ETA saved`);
      onLoadChange && onLoadChange();
      setEtaOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save ETA");
    }
  };
  const lane = LANE_OF[truck.status] || "loading";
  const meta = LANE_META[lane];
  const batchStatus = batch?.status;
  const docket = docketByTruckJob;

  // Determine current display state + the next-action label
  let stateLabel, nextLabel, canAdvance;
  const hasActiveBatch = batchStatus === "queued" || batchStatus === "batching";

  if (truck.status === "loading" && !hasActiveBatch) {
    stateLabel = "READY"; nextLabel = "→ LOAD"; canAdvance = true;
  } else if (hasActiveBatch) {
    stateLabel = "BATCHING"; nextLabel = "→ LOAD DONE"; canAdvance = true;
  } else if (truck.status === "en_route") {
    stateLabel = "EN ROUTE"; nextLabel = "→ ARRIVED"; canAdvance = true;
  } else if (truck.status === "on_site") {
    stateLabel = "ON SITE"; nextLabel = "→ DISCHARGE"; canAdvance = true;
  } else if (truck.status === "discharging") {
    stateLabel = "DISCHARGING"; nextLabel = "→ DELIVERED"; canAdvance = true;
  } else if (truck.status === "returning") {
    stateLabel = "RETURN"; nextLabel = "→ YARD"; canAdvance = true;
  } else {
    stateLabel = (truck.status || "?").toUpperCase(); nextLabel = ""; canAdvance = false;
  }

  const showPulse = hasActiveBatch || truck.status === "discharging";

  // v3.10a · GPS-dark detection + manual-depart fallback.
  // When a moving truck hasn't pinged in > 2 min AND the GPS-gated running
  // flag is still False, we surface a "Depart Now" button so the timer can
  // still be started by hand. This avoids the timer sitting frozen just
  // because a driver phone dropped signal.
  const gpsDark = !!truck.gps_dark;
  const outboundStale = truck.status === "en_route" && !initialEta.eta_to_site_running && gpsDark;
  const inboundStale  = truck.status === "returning" && !initialEta.eta_back_running && gpsDark;
  const showManualDepart = outboundStale || inboundStale;
  const manualDepart = async (e) => {
    e.stopPropagation();
    try {
      await api.post(`/jobs/${job.id}/manual-depart`, {
        truck_id: truck.id,
        leg: outboundStale ? "plant" : "site",
      });
      toast.success(`${truck.number} · manual depart · timer started`);
      onLoadChange && onLoadChange();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to record departure");
    }
  };

  // Waiting-time — compute live from docket.arrived_at once truck is on-site or discharging
  const showWait = (truck.status === "on_site" || truck.status === "discharging") && (docket?.arrived_at || docket?.wait_start_at);
  const wait = showWait ? computeWait(docket.arrived_at || docket.wait_start_at) : null;

  const handleAdvance = (e) => {
    e.stopPropagation();
    if (!canAdvance || isCompleted) return;
    onAdvance(truck, job, batch, docket);
  };

  return (
    <div className="inline-flex flex-col items-stretch">
      <div
        className={`inline-flex items-stretch border bg-surface hover:bg-surface-2 transition-colors ${showPulse ? "pulse-orange" : ""}`}
        style={{ borderColor: meta.color }}
      >
        <button
          type="button"
          data-testid={`truck-chip-${truck.number}-${job.id}`}
          onClick={handleAdvance}
          title={canAdvance ? `Click to advance: ${nextLabel.replace("→ ", "")}` : stateLabel}
          className="flex items-center gap-1.5 px-2 py-1 mono text-[11px]"
          style={{ color: meta.color }}
        >
          <Truck className="w-3 h-3" />
          <span className="font-bold text-ink">{truck.number}</span>
          <span className="text-[8px] tracking-widest opacity-80">{stateLabel}</span>
          {canAdvance && !isCompleted && (
            <span className="text-[8px] tracking-widest text-primary-orange font-bold">{nextLabel}</span>
          )}
        </button>
        {/* Per-truck ETA chip · v3.6o — click to set ETA to site + back to
            plant. Replaces the older m³ edit chip (moved into the ⋮ menu).
            Shows a subtle AUTO pill when the back-to-plant ETA was
        {/* Per-truck ETA chip · v3.6o — click to set ETA to site + back to
            plant. Replaces the older m³ edit chip (moved into the ⋮ menu).
            v3.6p — compact: single countdown for the truck's current phase
            (→site while loading/en-route, ←plant while on-site/returning).
            Full HH:MM detail lives in the popover. Shows a subtle AUTO pill
            when the back-to-plant ETA was auto-computed from live GPS. */}
        {(() => {
          // Pick the "next thing" ETA based on truck phase
          const isOutbound = truck.status === "loading" || truck.status === "slumping" || truck.status === "en_route" || !truck.status;
          const primary = isOutbound ? etaToSite : etaBack;
          const arrow = isOutbound ? "→" : "←";
          // v3.10 — Only tick the live countdown once the truck's GPS confirms
          // it has physically departed. The backend GPS handler stamps
          // eta_to_site_running / eta_back_running the first time a truck sits
          // > 200 m from the plant (outbound) or > 200 m from the site (return).
          // Before that we still show the planned time as a static "~HH:MM"
          // preview so dispatch can plan, but no colored countdown.
          const running = isOutbound
            ? !!initialEta.eta_to_site_running
            : !!initialEta.eta_back_running;
          const st = running
            ? etaStatusC(primary, nowMs)
            : { text: "", color: "text-muted-industrial", flash: false, minutes: null };
          const hasAny = !!(etaToSite || etaBack);
          const compact = running
            ? (st.text || primary || "")
            : (primary ? `~${primary}` : "");
          return (
            <Popover open={etaOpen} onOpenChange={(v) => { if (isCompleted && v) return; setEtaOpen(v); }}>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  data-testid={`truck-eta-chip-${truck.number}-${job.id}`}
                  onClick={(e) => e.stopPropagation()}
                  disabled={isCompleted}
                  title={isCompleted
                    ? "Job complete"
                    : `→ site ${etaToSite || "--:--"} · ← plant ${etaBack || "--:--"}`}
                  className={`px-1.5 flex items-center gap-0.5 mono text-[10px] border-l tabular-nums transition-colors ${
                    isCompleted
                      ? "text-muted-industrial cursor-not-allowed"
                      : st.flash
                        ? "text-red-400 hover:bg-red-500/10 animate-pulse"
                        : "text-primary-orange hover:bg-primary-orange hover:text-black"
                  }`}
                  style={{ borderColor: meta.color }}
                >
                  {hasAny ? (
                    <>
                      <span className="opacity-70">{arrow}</span>
                      <span className={st.color}>{compact}</span>
                      {etaAuto && (
                        <span
                          data-testid={`truck-eta-auto-${truck.number}-${job.id}`}
                          className="w-1 h-1 rounded-full bg-primary-orange ml-0.5"
                          title="Auto-computed from live GPS"
                        />
                      )}
                    </>
                  ) : (
                    <>
                      <Clock className="w-2.5 h-2.5" />
                      <span className="text-[9px] uppercase tracking-widest">ETA</span>
                    </>
                  )}
                </button>
              </PopoverTrigger>
              <PopoverContent
                align="end"
                side="bottom"
                className="rounded-none bg-surface border border-industrial p-3 w-64"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="mono text-[10px] uppercase tracking-widest text-muted-industrial mb-2 flex items-center gap-1.5">
                  <Clock className="w-3 h-3" />ETA — Truck {truck.number}
                </div>
                <div className="space-y-3">
                  <div>
                    <Label className="text-[10px] mono uppercase tracking-widest text-muted-industrial mb-1 flex items-center gap-1">→ Arrive at site</Label>
                    <Input
                      data-testid={`truck-eta-to-site-${truck.number}-${job.id}`}
                      type="time"
                      value={etaToSite}
                      onChange={(e) => setEtaToSite(e.target.value)}
                      className="rounded-none bg-transparent border-industrial mono text-xs h-8"
                    />
                  </div>
                  <div>
                    <Label className="text-[10px] mono uppercase tracking-widest text-muted-industrial mb-1 flex items-center gap-1">
                      ← Back at plant
                      {etaAuto && <span className="text-[8px] border border-primary-orange/60 text-primary-orange px-1 leading-none ml-1">AUTO</span>}
                    </Label>
                    <Input
                      data-testid={`truck-eta-back-${truck.number}-${job.id}`}
                      type="time"
                      value={etaBack}
                      onChange={(e) => setEtaBack(e.target.value)}
                      className="rounded-none bg-transparent border-industrial mono text-xs h-8"
                    />
                    {etaAuto && (
                      <div className="text-[9px] text-muted-industrial mt-1">
                        Auto-set from GPS when truck flipped to Returning. Editing here overrides until the next return.
                      </div>
                    )}
                  </div>
                  <div className="flex justify-end gap-2 pt-1">
                    <Button
                      variant="outline"
                      className="rounded-none border-industrial uppercase text-[10px] h-7"
                      onClick={() => setEtaOpen(false)}
                    >Cancel</Button>
                    <Button
                      data-testid={`truck-eta-save-${truck.number}-${job.id}`}
                      className="rounded-none bg-primary-orange hover:bg-[rgb(var(--primary-hover))] text-black uppercase text-[10px] font-bold tracking-widest h-7"
                      onClick={commitEta}
                    >Save</Button>
                  </div>
                </div>
              </PopoverContent>
            </Popover>
          );
        })()}
        {prediction && !isCompleted && (
          <span
            data-testid={`truck-predicted-return-${truck.number}-${job.id}`}
            onClick={(e) => e.stopPropagation()}
            title={`AI Turnaround Predictor · back at plant ~${prediction.predicted_return_local} (${prediction.minutes_remaining} min) · discharge median ${prediction.discharge_med_min} min · return median ${prediction.return_med_min} min · ${prediction.confidence} confidence`}
            className={`px-1.5 flex items-center gap-0.5 mono text-[10px] border-l tabular-nums ${prediction.confidence === "high" ? "text-emerald-400" : prediction.confidence === "medium" ? "text-sky-400" : "text-muted-industrial"}`}
          >
            ↺{prediction.predicted_return_local}
          </span>
        )}

        {/* v3.10a · GPS-dark fallback: manual "DEPART NOW" button appears only
            when GPS has gone dark AND the auto-gated running flag is still
            False. Keeps operators unblocked when a driver phone drops signal. */}
        {showManualDepart && (
          <button
            type="button"
            data-testid={`truck-manual-depart-${truck.number}-${job.id}`}
            onClick={manualDepart}
            title={outboundStale
              ? "GPS gone dark · start the outbound countdown by hand"
              : "GPS gone dark · start the return countdown by hand"}
            className="px-1.5 flex items-center gap-0.5 mono text-[9px] tracking-widest uppercase font-bold border-l bg-red-600/20 text-red-300 hover:bg-red-600 hover:text-white"
            style={{ borderColor: meta.color }}
          >
            <AlertTriangle className="w-2.5 h-2.5 animate-pulse" />
            <span>Depart</span>
          </button>
        )}
        {/* v3.10a · GPS "gone dark" pill — always visible when a moving truck
            hasn't pinged in > 2 min so dispatch can hunt the driver. */}
        {truck.in_maps && (
          <span
            data-testid={`truck-in-maps-${truck.number}-${job.id}`}
            title="Driver is navigating in Google Maps — GPS resumes when they come back to the app"
            className="px-1 flex items-center gap-0.5 mono text-[9px] tracking-widest uppercase font-bold border-l text-sky-300 bg-sky-950/40"
            style={{ borderColor: meta.color }}
          >
            <span className="w-1 h-1 rounded-full bg-sky-400" />
            IN MAPS
          </span>
        )}
        {gpsDark && (truck.status === "en_route" || truck.status === "on_site" || truck.status === "discharging" || truck.status === "returning") && (
          <span
            data-testid={`truck-gps-dark-${truck.number}-${job.id}`}
            title="Driver GPS hasn't reported for > 2 minutes"
            className="px-1 flex items-center gap-0.5 mono text-[9px] tracking-widest uppercase font-bold border-l text-red-400 bg-red-950/40"
            style={{ borderColor: meta.color }}
          >
            <span className="w-1 h-1 rounded-full bg-red-500 animate-pulse" />
            DARK
          </span>
        )}

        {/* Edge actions popover */}
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <button
              type="button"
              data-testid={`truck-more-${truck.number}-${job.id}`}
              onClick={(e) => e.stopPropagation()}
              className="px-1 border-l flex items-center hover:bg-surface-2"
              style={{ borderColor: meta.color, color: meta.color }}
              title="More actions"
            >
              <MoreVertical className="w-3 h-3" />
            </button>
          </PopoverTrigger>
          <PopoverContent
            side="bottom"
            align="end"
            className="rounded-none bg-surface border-industrial p-0 w-[260px]"
            data-testid={`truck-popover-${truck.number}-${job.id}`}
          >
            <div className="p-3 border-b border-industrial">
              <div className="mono text-sm font-bold">{truck.number}</div>
              <div className="text-[10px] text-muted-industrial">{truck.driver_name || "No driver"} · {truck.capacity_m3}m³ · <span style={{ color: meta.color }}>{stateLabel}</span></div>
            </div>
            <div className="p-2 space-y-1.5">
              {/* Cancel Load — visible while a batch is queued / batching */}
              {hasActiveBatch && !isCompleted && (
                <button
                  data-testid={`cancel-batch-${truck.number}-${job.id}`}
                  onClick={() => {
                    if (window.confirm(`Cancel load ${truck.number} for ${job.customer_name}? Truck returns to yard.`)) {
                      onCancelBatch(batch, truck); setOpen(false);
                    }
                  }}
                  className="w-full text-left border border-red-700 bg-red-500/5 hover:bg-red-500/10 px-3 py-2 flex items-center gap-2"
                >
                  <XOctagon className="w-3.5 h-3.5 text-red-500" />
                  <div>
                    <div className="mono text-xs font-bold uppercase tracking-widest text-red-500">Cancel Load</div>
                    <div className="text-[10px] text-muted-industrial">Abort batch · truck back to yard</div>
                  </div>
                </button>
              )}
              {docket && (
                <a
                  href={apiHref(`/dockets/${docket.id}/pdf`)}
                  target="_blank"
                  rel="noreferrer"
                  data-testid={`print-docket-${truck.number}-${job.id}`}
                  className="w-full block border border-industrial hover:border-primary-orange px-3 py-2 flex items-center gap-2"
                >
                  <Printer className="w-3.5 h-3.5 text-ink" />
                  <div>
                    <div className="mono text-xs font-bold uppercase tracking-widest text-ink">Print Docket</div>
                    {docket.docket_no && <div className="text-[10px] text-muted-industrial mono">{docket.docket_no}</div>}
                  </div>
                </a>
              )}
              {!isCompleted && !loadLocked && (
                <button
                  data-testid={`change-load-${truck.number}-${job.id}`}
                  onClick={() => { openLoadDialog(); setOpen(false); }}
                  className="w-full text-left border border-industrial hover:border-primary-orange px-3 py-2 flex items-center gap-2"
                >
                  <Pencil className="w-3.5 h-3.5 text-primary-orange" />
                  <div>
                    <div className="mono text-xs font-bold uppercase tracking-widest text-primary-orange">Change Load · {currentLoad} m³</div>
                    <div className="text-[10px] text-muted-industrial">Set the batched quantity for this truck</div>
                  </div>
                </button>
              )}
              {!isCompleted && (
                <button
                  data-testid={`swap-${truck.number}-${job.id}`}
                  onClick={() => { onSwap(truck); setOpen(false); }}
                  className="w-full text-left border border-industrial hover:border-primary-orange px-3 py-2 flex items-center gap-2"
                >
                  <RefreshCcw className="w-3.5 h-3.5 text-primary-orange" />
                  <div>
                    <div className="mono text-xs font-bold uppercase tracking-widest text-primary-orange">Swap Truck</div>
                    <div className="text-[10px] text-muted-industrial">Replace with another · keeps m³ intact</div>
                  </div>
                </button>
              )}
              {!isCompleted && (
                <button
                  data-testid={`unassign-${truck.number}-${job.id}`}
                  onClick={() => { onUnassign(truck); setOpen(false); }}
                  className="w-full text-left border border-industrial hover:border-red-700 px-3 py-2 flex items-center gap-2"
                >
                  <X className="w-3.5 h-3.5 text-muted-industrial" />
                  <div>
                    <div className="mono text-xs font-bold uppercase tracking-widest text-muted-industrial">Unassign</div>
                    <div className="text-[10px] text-muted-industrial">Send truck back to yard</div>
                  </div>
                </button>
              )}
            </div>
          </PopoverContent>
        </Popover>
      </div>

      {/* Wait-time pill — shown when on-site or discharging */}
      {wait && (
        <div
          data-testid={`wait-pill-${truck.number}-${job.id}`}
          className={`mono text-[9px] uppercase tracking-widest px-1.5 py-0.5 border-l border-r border-b ${
            wait.state === "grace"
              ? "border-industrial text-muted-industrial"
              : "border-red-500 text-red-500 bg-red-500/5 font-bold"
          }`}
          style={{ borderColor: wait.state === "grace" ? undefined : "#EF4444" }}
          title={wait.state === "grace"
            ? `Free wait grace — ${wait.minutesLeft} min until $${WAIT_RATE}/min kicks in`
            : `${wait.chargeMinutes} min over grace @ $${WAIT_RATE}/min = $${wait.charge.toFixed(2)}`}
        >
          {wait.state === "grace"
            ? <><Timer className="w-2.5 h-2.5 inline mr-1" />GRACE · {wait.minutesLeft}M LEFT</>
            : <><Timer className="w-2.5 h-2.5 inline mr-1" />WAIT · {wait.chargeMinutes}M · ${wait.charge.toFixed(0)}</>
          }
        </div>
      )}
      {/* Change Load Size dialog · v3.5n */}
      <Dialog open={loadDialogOpen} onOpenChange={setLoadDialogOpen}>
        <DialogContent className="rounded-none bg-surface border-industrial max-w-sm" data-testid={`load-dialog-${truck.number}-${job.id}`}>
          <DialogHeader>
            <DialogTitle className="heading uppercase text-primary-orange">
              Change Load · {truck.number}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="text-xs text-muted-industrial mono">
              <span>{job.customer_name}</span> · MIX {job.mix_code}
            </div>
            <div>
              <Label className="label-tag">Load Size (m³)</Label>
              <Input
                data-testid={`load-input-${truck.number}-${job.id}`}
                type="number"
                step="0.5"
                min="0.5"
                autoFocus
                value={loadVal}
                onChange={(e) => setLoadVal(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") commitLoad();
                  if (e.key === "Escape") setLoadDialogOpen(false);
                }}
                className="rounded-none bg-transparent border-industrial mono text-2xl h-14 text-right"
              />
              <div className="text-[10px] mono uppercase tracking-widest text-muted-industrial mt-1">
                Truck capacity {truck.capacity_m3} m³
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setLoadDialogOpen(false)} className="rounded-none border-industrial uppercase text-xs">Cancel</Button>
            <Button data-testid={`load-save-${truck.number}-${job.id}`} onClick={commitLoad} className="rounded-none bg-primary-orange hover:bg-[rgb(var(--primary-hover))] uppercase tracking-widest text-xs font-bold">
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// v3.6q — Ordered qty chip · click to top-up mid-pour.
// Customer rings ("send one more"), operator clicks the qty, picks +0.5/+1.0/+2.0
// (or types a custom delta), and quantity_m3 climbs on the spot with an audit
// entry on `job.topups[]`. Same widget also handles trimming (-0.5 etc.).
function JobTopupChip({ job, onSaved, disabled }) {
  const [open, setOpen] = useState(false);
  const [delta, setDelta] = useState("1");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const grew = Array.isArray(job.topups) && job.topups.length > 0;

  const commit = async (raw) => {
    const d = parseFloat(raw !== undefined ? raw : delta);
    if (isNaN(d) || d === 0) { toast.error("Enter a non-zero m³ delta"); return; }
    setSaving(true);
    try {
      await api.post(`/jobs/${job.id}/topup`, {
        delta_m3: d,
        note: (note || "").trim() || null,
      });
      toast.success(`${d > 0 ? "+" : ""}${d} m³ · order now ${(Number(job.quantity_m3) + d).toFixed(1)}`);
      setOpen(false);
      setNote("");
      setDelta("1");
      onSaved && onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Top-up failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Popover open={open} onOpenChange={(v) => { if (disabled && v) return; setOpen(v); }}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={`job-topup-chip-${job.id}`}
          onClick={(e) => e.stopPropagation()}
          disabled={disabled}
          title={disabled ? "Order closed" : `Click to add or trim m³ mid-pour${grew && job.original_quantity_m3 != null ? ` · was ${job.original_quantity_m3}` : ""}`}
          className={`group inline-flex items-center gap-1 leading-none ${
            disabled ? "cursor-not-allowed opacity-60" : "hover:text-primary-orange cursor-pointer"
          }`}
        >
          <span className="mono text-lg font-bold text-ink">{job.quantity_m3}</span>
          <span className="text-[10px] text-muted-industrial ml-0.5">m³</span>
          {!disabled && (
            <Plus className="w-3 h-3 text-primary-orange opacity-0 group-hover:opacity-100 transition-opacity" />
          )}
          {grew && (
            <span
              data-testid={`job-topup-badge-${job.id}`}
              className="w-1 h-1 rounded-full bg-primary-orange"
              title={`Topped up ${job.topups.length}× — click for detail`}
            />
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        side="bottom"
        className="rounded-none bg-surface border border-industrial p-3 w-72"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mono text-[10px] uppercase tracking-widest text-muted-industrial mb-2 flex items-center gap-1.5">
          <Plus className="w-3 h-3" />Add / Trim m³ · Current {job.quantity_m3}
        </div>
        <div className="flex items-center gap-1.5 mb-2">
          {[-0.5, +0.5, +1, +2].map((n) => (
            <button
              key={n}
              data-testid={`job-topup-quick-${job.id}-${n}`}
              onClick={() => commit(n)}
              disabled={saving}
              className={`px-2 py-1 mono text-[11px] font-bold border transition-colors ${
                n < 0
                  ? "border-red-700 text-red-400 hover:bg-red-500/10"
                  : "border-primary-orange text-primary-orange hover:bg-primary-orange hover:text-black"
              }`}
            >
              {n > 0 ? `+${n}` : n} m³
            </button>
          ))}
        </div>
        <div className="flex items-center gap-1.5">
          <Input
            data-testid={`job-topup-custom-${job.id}`}
            type="number"
            step="0.1"
            value={delta}
            onChange={(e) => setDelta(e.target.value)}
            placeholder="±m³"
            className="rounded-none bg-transparent border-industrial mono text-xs h-8 flex-1"
          />
          <Button
            data-testid={`job-topup-save-${job.id}`}
            onClick={() => commit()}
            disabled={saving}
            className="rounded-none bg-primary-orange hover:bg-[rgb(var(--primary-hover))] text-black uppercase text-[10px] font-bold tracking-widest h-8"
          >Apply</Button>
        </div>
        <Input
          data-testid={`job-topup-note-${job.id}`}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Note (optional) — 'site called', 'trim short pour'"
          className="rounded-none bg-transparent border-industrial mono text-[10px] h-7 mt-2"
        />
        {grew && (
          <div className="mt-3 pt-2 border-t border-industrial">
            <div className="mono text-[9px] uppercase tracking-widest text-muted-industrial mb-1">Recent Top-ups</div>
            <div className="space-y-0.5 max-h-24 overflow-y-auto">
              {[...job.topups].reverse().slice(0, 5).map((t, i) => (
                <div key={i} className="mono text-[9px] text-muted-industrial flex items-center justify-between gap-2">
                  <span>
                    <span className={t.delta_m3 > 0 ? "text-primary-orange" : "text-red-400"}>
                      {t.delta_m3 > 0 ? "+" : ""}{t.delta_m3}
                    </span>
                    {" → "}{t.to_m3} m³
                  </span>
                  <span className="text-[8px] opacity-70 truncate max-w-[140px]" title={t.note || ""}>
                    {t.note || (t.by || "").split("@")[0]}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}


/* v3.10c · Compact-View truck micro-badge.
 * Renders the truck number + a 1-char status glyph in a 26 px chip. On hover,
 * shows a compact floating card with:
 *   • Current volume (m³)  — from job.truck_loads[truck.id] || truck.capacity_m3
 *   • Live ETA             — from job.truck_etas[truck.id]
 *   • Last Status Update   — from truck.location_updated_at (falls back to
 *                            docket timestamps if no GPS is coming through)
 * Bright red flashing style is preserved when the truck's pre-start hasn't
 * been submitted for today so the Safety warning stays visible.
 */
function TruckMicroBadge({ truck, job, docket, needsPreStart, prediction }) {
  const STATUS_GLYPH = {
    loading:     { txt: "L",  hue: "#EAB308", label: "LOADING" },
    en_route:    { txt: "→",  hue: "#F97316", label: "TO JOB" },
    on_site:     { txt: "S",  hue: "#A855F7", label: "ON SITE" },
    discharging: { txt: "D",  hue: "#A855F7", label: "DISCHARGING" },
    returning:   { txt: "←",  hue: "#0EA5E9", label: "RETURNING" },
    available:   { txt: "✓",  hue: "#22C55E", label: "AVAILABLE" },
    out_of_service: { txt: "×", hue: "#71717A", label: "OUT" },
  };
  const g = STATUS_GLYPH[truck.status] || { txt: "•", hue: "#71717A", label: (truck.status || "").toUpperCase() };
  const eta = ((job.truck_etas || {})[truck.id]) || {};
  const load = job.truck_loads?.[truck.id];
  const _vcap = Number(truck.capacity_m3 || 0);
  const _vowed = Math.max(0, Number(job.quantity_m3 || 0) - Number(job.delivered_m3 || 0));
  const volume = (load != null && Number(load) > 0) ? Number(load)
    : (_vowed > 0 ? Math.min(_vcap || _vowed, _vowed) : _vcap);
  const running = truck.status === "en_route" ? !!eta.eta_to_site_running : truck.status === "returning" ? !!eta.eta_back_running : false;
  const primaryEta = truck.status === "returning" ? eta.eta_back_to_plant : eta.eta_to_site;
  const lastUpdate = truck.location_updated_at || docket?.updated_at || docket?.arrived_at || docket?.batched_at;
  const fmtAgo = (iso) => {
    if (!iso) return "no ping";
    try {
      const s = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
      if (s < 60) return `${s}s ago`;
      if (s < 3600) return `${Math.floor(s / 60)}m ${s % 60}s ago`;
      return `${Math.floor(s / 3600)}h ${Math.floor((s % 3600) / 60)}m ago`;
    } catch { return "—"; }
  };
  return (
    <div className="relative group" data-testid={`truck-micro-${truck.number}-${job.id}`}>
      <div
        title={`${truck.number} · ${g.label}`}
        className={`inline-flex items-center gap-1 px-1.5 h-6 border ${needsPreStart ? "pulse-red-strong border-red-500" : ""}`}
        style={needsPreStart ? {} : { borderColor: g.hue }}
      >
        <span
          className={`mono text-[10px] font-black leading-none ${needsPreStart ? "text-red-400 flash-red" : ""}`}
          style={needsPreStart ? {} : { color: g.hue }}
        >
          {truck.number}
        </span>
        <span
          className="mono text-[10px] font-bold leading-none w-3 text-center"
          style={{ color: needsPreStart ? "#EF4444" : g.hue }}
        >
          {g.txt}
        </span>
      </div>
      {/* Hover-to-expand tooltip (CSS-only, appears above the chip). */}
      <div
        role="tooltip"
        data-testid={`truck-micro-tooltip-${truck.number}-${job.id}`}
        className="opacity-0 group-hover:opacity-100 pointer-events-none absolute bottom-full left-1/2 -translate-x-1/2 mb-1 z-40 w-[180px] p-2 bg-surface border-2 border-primary-orange shadow-lg transition-opacity duration-100"
      >
        <div className="flex items-center justify-between mb-1.5">
          <span className="mono text-[10px] font-black text-primary-orange">{truck.number}</span>
          <span
            className="mono text-[8px] uppercase tracking-widest px-1 py-0.5"
            style={{ background: g.hue, color: "#000" }}
          >
            {g.label}
          </span>
        </div>
        <div className="grid grid-cols-2 gap-x-2 gap-y-1 text-[10px]">
          <div className="text-muted-industrial mono uppercase">Volume</div>
          <div className="mono text-right font-bold" data-testid={`truck-micro-volume-${truck.number}-${job.id}`}>
            {volume.toFixed(1)} m³
          </div>
          <div className="text-muted-industrial mono uppercase">ETA</div>
          <div className="mono text-right font-bold" data-testid={`truck-micro-eta-${truck.number}-${job.id}`}>
            {primaryEta ? (running ? primaryEta : `~${primaryEta}`) : "—"}
          </div>
          <div className="text-muted-industrial mono uppercase">Updated</div>
          <div
            className="mono text-right font-bold"
            data-testid={`truck-micro-updated-${truck.number}-${job.id}`}
          >
            {fmtAgo(lastUpdate)}
          </div>
          {prediction && (
            <>
              <div className="text-muted-industrial mono uppercase">AI return</div>
              <div className={`mono text-right font-bold ${prediction.confidence === "high" ? "text-emerald-400" : prediction.confidence === "medium" ? "text-sky-400" : ""}`}
                data-testid={`truck-predicted-return-${truck.number}-${job.id}`} title={`${prediction.minutes_remaining} min · ${prediction.confidence} confidence`}>
                ↺ {prediction.predicted_return_local}
              </div>
            </>
          )}
        </div>
        {needsPreStart && (
          <div className="mt-1.5 pt-1.5 border-t border-red-800 mono text-[9px] uppercase tracking-widest text-red-400 flash-red">
            Pre-start not submitted
          </div>
        )}
      </div>
    </div>
  );
}


export default function Command() {
  const today = localToday();
  const { plantId } = usePlant();
  const [viewDate, setViewDate] = useState(today);
  // Deep-link support: /dispatch?date=YYYY-MM-DD (used from Portal Requests inbox
  // + Day Planner so newly-approved jobs land on the right day automatically).
  const [searchParams] = useSearchParams();
  useEffect(() => {
    const qDate = searchParams.get("date");
    if (qDate && /^\d{4}-\d{2}-\d{2}$/.test(qDate)) setViewDate(qDate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);
  const [dash, setDash] = useState(null);
  const [trucks, setTrucks] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [mixes, setMixes] = useState([]);
  const [materials, setMaterials] = useState([]);
  const { org } = useAuth();
  const fleet = isFleet(org);
  const [batches, setBatches] = useState([]);
  const [dockets, setDockets] = useState([]);
  const [extraTags, setExtraTags] = useState([]);
  const [surcharges, setSurcharges] = useState([]);
  useEffect(() => { api.get("/surcharges").then((r) => setSurcharges(r.data || [])).catch(() => {}); }, []);
  const [draggedTruck, setDraggedTruck] = useState(null);
  // v3.16 · Compact / Focus mode — collapse the Fleet Kanban and toolbar KPIs
  // to hand every pixel back to the job list & map. Persisted per-user.
  const [fleetCollapsed, setFleetCollapsed] = useState(() => {
    try { return localStorage.getItem("gt.dispatch.fleetCollapsed") === "1"; } catch (_) { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem("gt.dispatch.fleetCollapsed", fleetCollapsed ? "1" : "0"); } catch (_) {}
  }, [fleetCollapsed]);
  const [focusMode, setFocusMode] = useState(() => {
    try { return localStorage.getItem("gt.dispatch.focusMode") === "1"; } catch (_) { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem("gt.dispatch.focusMode", focusMode ? "1" : "0"); } catch (_) {}
  }, [focusMode]);
  const [dragOverJob, setDragOverJob] = useState(null);
  const [newJobOpen, setNewJobOpen] = useState(false);
  useEffect(() => {
    if (searchParams.get("new") === "1") setNewJobOpen(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);
  const [editJob, setEditJob] = useState(null);
  const [optimiseJobId, setOptimiseJobId] = useState(null);
  const [smsCompose, setSmsCompose] = useState(null); // { phone, message, trackingUrl, customerName }
  const [editForm, setEditForm] = useState({ date: "", time_required: "", quantity_m3: 0, po_number: "", ordered_by: "", site_contact_phone: "", site_id: "", extras: "", mix_id: "", extra_tag_ids: [], delivery_km: "", mini_load_surcharge: false, ex_plant: false });
  const [repricing, setRepricing] = useState(null); // { current_rate, dockets, total_delta, selected: Set }
  const [form, setForm] = useState({ customer_id: "", is_cash_sale: false, myob_payment_method: "", walk_in_name: "", walk_in_phone: "", walk_in_email: "", site_id: "", site_name: "", site_address: "", save_site: true, use_custom_site: false, mix_id: "", material_id: "", unit: "m3", quantity_m3: 8, date: today, time_required: "09:00", po_number: "", ordered_by: "", site_contact_phone: "", notes: "", notes_to_driver: true, extras: "", extra_tag_ids: [], surcharge_ids: [], delivery_km: "", trucks_needed: "", load_spacing_min: "", pump_company_id: "", pump_unit_id: "", mini_load_surcharge: false, ex_plant: false, extra_lines: [] });
  const [pumpCompanies, setPumpCompanies] = useState([]);
  const [previewKm, setPreviewKm] = useState({ km: null, fee: 0, loading: false });
  // Out-of-hours fee: auto-tick when the pour time falls outside trading hours.
  // Dispatch can untick it and we won't fight them (oohDismissed).
  const oohSurcharge = findOohSurcharge(surcharges);
  const jobIsOoh = isOutOfHours(form.date, form.time_required);
  const [oohDismissed, setOohDismissed] = useState(false);
  useEffect(() => {
    if (!oohSurcharge) return;
    const has = (form.surcharge_ids || []).includes(oohSurcharge.id);
    if (jobIsOoh && !has && !oohDismissed) {
      setForm((f) => ({ ...f, surcharge_ids: [...(f.surcharge_ids || []), oohSurcharge.id] }));
    } else if (!jobIsOoh && has) {
      setForm((f) => ({ ...f, surcharge_ids: (f.surcharge_ids || []).filter((x) => x !== oohSurcharge.id) }));
      setOohDismissed(false);
    }
  }, [jobIsOoh, oohSurcharge, form.surcharge_ids, oohDismissed]);

  // v3.7 — Per-truck pre-start snapshot so the dispatch board can flash red on
  // any truck that hasn't submitted its pre-start today AND intercept drag-to-
  // assign until the operator explicitly overrides the safety warning.
  const [preStartByTruck, setPreStartByTruck] = useState({}); // {truckId: {submitted, overall, resolved}}
  const [assignWarn, setAssignWarn] = useState(null);          // {truck, job}
  // v3.16 · Part-load prompt state — opens right after a truck is dropped.
  const [loadPrompt, setLoadPrompt] = useState(null);          // {job, truck, value}
  const [loadPromptSaving, setLoadPromptSaving] = useState(false);
  const [override, setOverride] = useState({});                // {truckId: true}
  const nav = useNavigate();

  useEffect(() => {
    api.get("/pump-companies").then((r) => setPumpCompanies(r.data)).catch(() => {});
    api.get("/materials").then((r) => setMaterials((r.data || []).filter((m) => m.active !== false))).catch(() => {});
  }, []);

  // Live km preview when the user types an address / picks a site
  useEffect(() => {
    if (!newJobOpen) return;
    // If a km was manually typed we skip the auto lookup
    if (form.delivery_km !== "" && form.delivery_km != null && !isNaN(Number(form.delivery_km))) {
      setPreviewKm({ km: Number(form.delivery_km), fee: Math.max(0, Number(form.delivery_km) - 25) * 2, loading: false });
      return;
    }
    let addr = "";
    let site_id = "";
    if (form.use_custom_site) {
      addr = (form.site_address || "").trim();
    } else if (form.site_id) {
      site_id = form.site_id;
    }
    if (!addr && !site_id) {
      setPreviewKm({ km: null, fee: 0, loading: false });
      return;
    }
    setPreviewKm((p) => ({ ...p, loading: true }));
    const handle = setTimeout(async () => {
      try {
        const { data } = await api.post("/geocode/preview-km", {
          address: addr || undefined,
          site_id: site_id || undefined,
          customer_id: form.customer_id || undefined,
          plant_id: plantId,
        });
        setPreviewKm({ km: data.km, fee: data.fee || 0, loading: false });
      } catch {
        setPreviewKm({ km: null, fee: 0, loading: false });
      }
    }, 600);
    return () => clearTimeout(handle);
  }, [newJobOpen, form.site_address, form.site_id, form.use_custom_site, form.customer_id, form.delivery_km, plantId]);
  const [liveStatus, setLiveStatus] = useState("connecting");
  const [, forceTick] = useState(0);
  const [nowMs, setNowMs] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNowMs(Date.now()), 30000);
    return () => clearInterval(t);
  }, []);
  const [flashJobIds, setFlashJobIds] = useState(new Set());
  // v3.10c · Compact View — a high-density job list mode that swaps the fat
  // TruckChip for a micro-badge with a hover tooltip. Persisted per browser so
  // the 5 AM crew doesn't have to re-toggle every session.
  const [compactView, setCompactView] = useState(() => {
    try {
      const raw = localStorage.getItem("gadtech.dispatch.compactView");
      return raw === null ? true : raw === "1";
    } catch { return true; }
  });
  useEffect(() => {
    try { localStorage.setItem("gadtech.dispatch.compactView", compactView ? "1" : "0"); } catch { /* ignore */ }
  }, [compactView]);
  // v3.10a · Track which trucks we've already alerted for "GPS gone dark" so
  // we don't spam the same toast on every 30-s reload. Persist through a
  // ref (not state) — we don't need re-renders when the set changes.
  const darkAlertedRef = useRef(new Set());
  const wsRef = useRef(null);
  const reloadTimerRef = useRef(null);
  const flashTimersRef = useRef(new Map());
  const trucksRef = useRef([]);
  const dashRef = useRef(null);
  useEffect(() => { trucksRef.current = trucks; }, [trucks]);
  useEffect(() => { dashRef.current = dash; }, [dash]);

  // Flash a job row briefly (orange pulse) when a truck attached to it changes stage
  const flashJob = useCallback((jobId) => {
    if (!jobId) return;
    setFlashJobIds((prev) => {
      const next = new Set(prev);
      next.add(jobId);
      return next;
    });
    if (flashTimersRef.current.has(jobId)) clearTimeout(flashTimersRef.current.get(jobId));
    const tid = setTimeout(() => {
      setFlashJobIds((prev) => {
        const next = new Set(prev);
        next.delete(jobId);
        return next;
      });
      flashTimersRef.current.delete(jobId);
    }, 1800);
    flashTimersRef.current.set(jobId, tid);
  }, []);

  // Refresh wait-time chip readouts every 15s
  useEffect(() => {
    const id = setInterval(() => forceTick((n) => n + 1), 15000);
    return () => clearInterval(id);
  }, []);

  // AI Turnaround Predictor · truck_id → predicted plant-return (refreshed each minute)
  const [predictions, setPredictions] = useState({});
  useEffect(() => {
    let dead = false;
    const pull = () => api.get(`/trucks/turnaround-predictions${plantId ? `?plant_id=${plantId}` : ""}`)
      .then((r) => { if (!dead) setPredictions(Object.fromEntries((r.data.predictions || []).map((p) => [p.truck_id, p]))); })
      .catch(() => {});
    pull();
    const id = setInterval(pull, 20_000);
    return () => { dead = true; clearInterval(id); };
  }, [plantId]);

  const load = useCallback(async () => {
    const [d, t, c, m, b, dk, et, fh] = await Promise.all([
      api.get(`/dashboard?date=${viewDate}${plantId ? `&plant_id=${plantId}` : ""}`),
      api.get(`/trucks?${plantId ? `plant_id=${plantId}` : ""}`),
      api.get("/customers"),
      api.get("/mixes"),
      api.get("/batches"),
      api.get("/dockets"),
      api.get("/extra-tags"),
      api.get("/admin/fleet-health").catch(() => ({ data: [] })),
    ]);
    setDash(d.data);
    setTrucks(t.data);
    // v3.10a · GPS-gone-dark toast — fire once per truck when it first flips
    // dark. Clears from the ref the moment GPS returns so re-losing signal
    // will re-alert.
    (t.data || []).forEach((tr) => {
      const isMoving = ["en_route", "on_site", "discharging", "returning"].includes(tr.status);
      if (isMoving && tr.gps_dark && !darkAlertedRef.current.has(tr.id)) {
        darkAlertedRef.current.add(tr.id);
        toast.error(`📡 ${tr.number} · GPS gone dark · check driver`, { duration: 8000 });
      }
      if (!tr.gps_dark && darkAlertedRef.current.has(tr.id)) {
        darkAlertedRef.current.delete(tr.id);
      }
    });
    setCustomers(c.data);
    setMixes(m.data);
    setBatches(b.data);
    setDockets(dk.data);
    setExtraTags(et.data);
    // v3.7 — Precompute fleet-health lookup for the red-flashing safety warning
    const map = {};
    (fh.data || []).forEach((r) => {
      map[r.truck.id] = {
        submitted: !!r.submitted_today,
        overall: r.latest_pre_start?.overall,
        resolved: !!r.latest_pre_start?.resolved,
        status: r.truck.status,
      };
    });
    setPreStartByTruck(map);
  }, [viewDate, plantId]);

  // Lightweight live refresh (dashboard + trucks only, ~30 KB) for the polling fallback.
  const loadLive = useCallback(async () => {
    try {
      const [d, t] = await Promise.all([
        api.get(`/dashboard?date=${viewDate}${plantId ? `&plant_id=${plantId}` : ""}`),
        api.get(`/trucks?${plantId ? `plant_id=${plantId}` : ""}`),
      ]);
      setDash(d.data);
      setTrucks(t.data);
    } catch { /* offline */ }
  }, [viewDate, plantId]);

  useEffect(() => { load(); }, [load]);

  // Arm audio on first gesture so the spoken "truck back" call-out is allowed by the browser.
  useEffect(() => { autoArmOnFirstGesture(); }, []);

  // Send / clear a returning-truck instruction (Wash Out / Wash Fins / Dump Leftovers).
  const toggleWash = useCallback(async (t, task) => {
    const active = !t.wash_tasks?.[task];
    try {
      await api.post(`/trucks/${t.id}/wash-task`, { task, active });
      loadLive();
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't send instruction"); }
  }, [loadLive]);

  // Offline queue drained → pull the authoritative board
  useEffect(() => {
    const h = () => load();
    window.addEventListener(SYNCED_EVENT, h);
    return () => window.removeEventListener(SYNCED_EVENT, h);
  }, [load]);

  useEffect(() => {
    let ws;
    let closed = false;
    let retry = 0;
    const debounced = () => {
      if (reloadTimerRef.current) clearTimeout(reloadTimerRef.current);
      reloadTimerRef.current = setTimeout(() => load(), 250);
    };
    const connect = () => {
      if (closed) return;
      setLiveStatus("connecting");
      try { ws = new WebSocket(`${WS_BASE}/ws/dispatch`); }
      catch { setLiveStatus("offline"); return; }
      wsRef.current = ws;
      ws.onopen = () => { retry = 0; setLiveStatus("live"); };
      ws.onmessage = (e) => {
        try {
          const evt = JSON.parse(e.data);
          if (evt.type === "hello") return;
          if (plantId && evt.plant_id && evt.plant_id !== plantId) return;
          // Flash job row(s) when a truck attached to them ticks through a stage
          if (evt.type === "truck.status" && evt.truck_id) {
            if (evt.arrived_yard) speakTruckBack(evt.truck_number);
            const truck = trucksRef.current.find((t) => t.id === evt.truck_id);
            const jobsList = (dashRef.current?.jobs) || [];
            const jobId = truck?.current_job_id || jobsList.find((j) => (j.assigned_truck_ids || []).includes(evt.truck_id))?.id;
            if (jobId) flashJob(jobId);
          } else if (evt.type === "batch.completed" || evt.type === "docket.cancelled" || evt.type === "docket.updated") {
            if (evt.job_id) flashJob(evt.job_id);
          }
          debounced();
        } catch { /* ignore */ }
      };
      ws.onclose = () => {
        setLiveStatus("offline");
        if (closed) return;
        retry = Math.min(retry + 1, 5);
        setTimeout(connect, 1000 * retry);
      };
      ws.onerror = () => { try { ws.close(); } catch { /* ignore */ } };
    };
    connect();
    // Fallback: the platform proxy does not pass WebSocket upgrades, so poll while the socket isn't live.
    const poll = setInterval(() => { if (document.hidden) return; if (!ws || ws.readyState !== WebSocket.OPEN) loadLive(); }, 12_000);
    return () => {
      closed = true;
      clearInterval(poll);
      if (reloadTimerRef.current) clearTimeout(reloadTimerRef.current);
      try { ws && ws.close(); } catch { /* ignore */ }
    };
  }, [load, loadLive, plantId]);

  const shiftDate = (delta) => setViewDate(shiftLocalDate(viewDate, delta));
  const goToday = () => setViewDate(today);
  const goTomorrow = () => setViewDate(shiftLocalDate(today, 1));

  // v3.16 · Manual per-job recount. Server sums live truck_loads + delivered
  // dockets and returns the authoritative delivered_m3. We patch just this
  // job in local state so the progressive bar snaps to the truth without
  // flashing the whole page.
  const recountJob = useCallback(async (jid) => {
    try {
      const r = await api.post(`/jobs/${jid}/recount`, {});
      const fresh = r.data;
      setDash((prev) => prev ? {
        ...prev,
        jobs: (prev.jobs || []).map((row) => row.id === jid ? { ...row, ...fresh } : row),
      } : prev);
      toast.success(`Recounted · ${fresh.delivered_m3?.toFixed(1) ?? "0"}m³ delivered`);
    } catch (err) {
      // If the backend endpoint isn't there for some reason, fall back to a
      // full refresh — still gets the user what they need.
      toast.info("Refreshing dispatch…");
      load();
    }
  }, [load]);

  // Drop = assign truck. Batching is a deliberate "START SEQUENCE" click on the chip.
  const handleDragStart = (t) => setDraggedTruck(t);
  const handleDragOver = (e, jid) => { e.preventDefault(); setDragOverJob(jid); };
  const handleDragLeave = () => setDragOverJob(null);
  const doAssign = async (job, truck) => {
    try {
      const { data: updated } = await api.post(`/jobs/${job.id}/assign-truck`, { truck_id: truck.id });
      toast.success(`${truck.number} → ${job.customer_name} · READY (click chip to load)`);
      // v3.17 · Pre-fill the load prompt with the AI-suggested even-split for
      // this truck (e.g. a 10 m³ job on an 8 m³ barrel → 5.0, not the full 8),
      // so the War Room's auto-split now also carries through on the Dispatch
      // board. Falls back to the barrel size only if the split is unavailable.
      const aiLoad = updated?.truck_loads?.[truck.id];
      setLoadPrompt({ job, truck, value: String(aiLoad ?? truck.capacity_m3 ?? "") });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Assign failed");
    }
  };

  const submitLoadPrompt = async () => {
    if (!loadPrompt) return;
    const qty = parseFloat(loadPrompt.value);
    if (!Number.isFinite(qty) || qty <= 0) {
      toast.error("Enter a load size greater than zero");
      return;
    }
    setLoadPromptSaving(true);
    try {
      await api.post(`/jobs/${loadPrompt.job.id}/truck-load`, {
        truck_id: loadPrompt.truck.id,
        quantity_m3: qty,
      });
      toast.success(`${loadPrompt.truck.number} · ${qty} m³ committed`);
      setLoadPrompt(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to set load");
    } finally {
      setLoadPromptSaving(false);
    }
  };
  const handleDrop = async (e, job) => {
    e.preventDefault();
    setDragOverJob(null);
    if (!draggedTruck) return;
    const truck = draggedTruck;
    setDraggedTruck(null);
    // v3.7 — Block assignment until the truck's pre-start is done (or the
    // dispatcher has explicitly overridden the warning for this session).
    const ps = preStartByTruck[truck.id];
    const needsWarning =
      !override[truck.id] && ps && (!ps.submitted || (ps.overall === "fail" && !ps.resolved));
    if (needsWarning) {
      setAssignWarn({ truck, job });
      return;
    }
    await doAssign(job, truck);
  };

  // Drop a truck onto a lane header → change truck.status manually. Used to
  // park a truck ("out of service") or nudge lifecycle when the driver forgets
  // to click through the sequence.
  const [dragOverLane, setDragOverLane] = useState(null);
  const handleLaneDragOver = (e, lane) => { e.preventDefault(); setDragOverLane(lane); };
  const handleLaneDragLeave = () => setDragOverLane(null);
  const handleLaneDrop = async (e, laneKey) => {
    e.preventDefault();
    setDragOverLane(null);
    if (!draggedTruck) return;
    const truck = draggedTruck;
    setDraggedTruck(null);
    const targetStatus = LANE_META[laneKey]?.status;
    if (!targetStatus || targetStatus === truck.status) return;
    // If the truck is on an active batch (loading/en_route/on_site/discharging)
    // and the user moves it back to available or out_of_service, warn but proceed.
    const inMotion = ["loading", "slumping", "en_route", "on_site", "discharging"].includes(truck.status);
    if (inMotion && (targetStatus === "available" || targetStatus === "out_of_service")) {
      const ok = window.confirm(
        `${truck.number} is currently ${truck.status.replace("_", " ")}. Moving it to ${LANE_META[laneKey].label} will detach it from the active job. Continue?`
      );
      if (!ok) return;
    }
    try {
      const payload = { status: targetStatus };
      if (targetStatus === "available" || targetStatus === "out_of_service") {
        payload.current_job_id = null;
      }
      await api.post(`/trucks/${truck.id}/status`, payload);
      toast.success(`${truck.number} → ${LANE_META[laneKey].label}`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Status change failed");
    }
  };

  const unassign = async (j, tid) => {
    try {
      await api.post(`/jobs/${j.id}/unassign-truck`, { truck_id: tid });
      toast.success("Unassigned");
      load();
    } catch { toast.error("Failed"); }
  };

  // Swap-truck flow — opens a picker so the dispatcher can replace one assigned
  // truck with another WITHOUT changing delivered_m3 (fixes wrong-truck-on-job).
  const [swapCtx, setSwapCtx] = useState(null); // { job, fromTruck }
  const openSwap = (j, fromTruck) => setSwapCtx({ job: j, fromTruck });
  const doSwap = async (toTruckId) => {
    if (!swapCtx) return;
    try {
      await api.post(`/jobs/${swapCtx.job.id}/swap-truck`, {
        from_truck_id: swapCtx.fromTruck.id,
        to_truck_id: toTruckId,
      });
      const toTruck = trucks.find((t) => t.id === toTruckId);
      toast.success(`Swapped ${swapCtx.fromTruck.number} → ${toTruck?.number || "new truck"} · m³ preserved`);
      setSwapCtx(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Swap failed");
    }
  };

  const cancelBatch = async (batch, truck) => {
    try {
      await api.post(`/batches/${batch.id}/cancel`, { reason: "Dispatcher cancelled" });
      toast.success(`Load cancelled · ${truck.number} back in yard`);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to cancel"); }
  };

  // Unified single-click state advancer.
  // Order: READY → START SEQUENCE → BATCHING → LOAD DONE → EN ROUTE → ARRIVED
  //     → ON SITE → DISCHARGE → DISCHARGING → DELIVERED → YARD
  const advance = async (truck, job, batch, docket) => {
    // Offline-safe: manual status stamps queue locally (real press time) and the board updates optimistically.
    let queuedTruckStatus = null;
    let queuedDocketStatus = null;
    const q = (res, truckStatus, docketStatus) => {
      if (!res?.data?.queued) return false;
      queuedTruckStatus = truckStatus; queuedDocketStatus = docketStatus || null;
      return true;
    };
    try {
      const bStat = batch?.status;
      const hasActive = bStat === "queued" || bStat === "batching";
      if (!hasActive && truck.status === "loading") {
        // READY → START SEQUENCE: create + start batch.
        // v3.6z — for multi-line jobs (e.g. Blockfill + 25MPa), pick the
        // next line that still has remaining qty so the batch loads the
        // right mix, not always the first line.
        const lines = job.lines || [];
        const nextLine = lines.find(
          (l) => (Number(l.quantity_m3) || 0) - (Number(l.delivered_m3) || 0) > 0.01
        );
        const remaining = nextLine
          ? Math.max(0, Number(nextLine.quantity_m3 || 0) - Number(nextLine.delivered_m3 || 0))
          : Math.max(0, (job.quantity_m3 || 0) - (job.delivered_m3 || 0));
        // Honour the AI even-split assigned at dispatch (job.truck_loads[truck.id]),
        // e.g. an 11 m³ job on a 7 m³ truck → 5.5, not the full barrel. Fall back
        // to capacity only when no split was assigned; always clamp to remaining.
        const split = job.truck_loads?.[truck.id];
        const base = (split != null && Number(split) > 0) ? Number(split) : (truck.capacity_m3 || 8);
        const qty = Math.min(base, remaining || base);
        const res = await api.post("/batches", {
          job_id: job.id,
          truck_id: truck.id,
          quantity_m3: qty,
          mix_id: nextLine ? nextLine.mix_id : undefined,
        });
        await api.post(`/batches/${res.data.id}/start`);
        toast.success(`Load started · ${truck.number} · ${qty}m³ · ${nextLine?.mix_code || job.mix_code || ""}`);
      } else if (hasActive) {
        // BATCHING → LOAD DONE: complete batch, create docket, truck → slumping
        const printTab = prepareAutoPrintTab();
        let res;
        try { res = await api.post(`/batches/${batch.id}/complete`); } catch (err) { finishAutoPrint(printTab, null); throw err; }
        finishAutoPrint(printTab, res.data?.auto_print === false ? null : res.data?.docket);
        toast.success(`Docket ${res.data?.docket?.docket_no || ""} · ${truck.number} → slumping`);
      } else if (truck.status === "slumping") {
        // SLUMPING → EN ROUTE: driver has finished slump test at plant
        const res = await api.post(`/jobs/${job.id}/manual-depart`, { truck_id: truck.id, leg: "plant" }, { offline: `${truck.number} departed plant` });
        if (!q(res, "en_route")) toast.success(`${truck.number} → en route`);
      } else if (truck.status === "en_route") {
        // EN ROUTE → ARRIVED (on_site) — starts wait clock
        const res = docket
          ? await api.post(`/dockets/${docket.id}/status`, { status: "on_site" }, { offline: `${truck.number} arrived on site` })
          : await api.post(`/trucks/${truck.id}/status`, { status: "on_site" }, { offline: `${truck.number} arrived on site` });
        if (!q(res, "on_site", "on_site")) toast.success(`${truck.number} → on site · wait clock started`);
      } else if (truck.status === "on_site") {
        // ON SITE → DISCHARGE
        const res = docket
          ? await api.post(`/dockets/${docket.id}/status`, { status: "discharging" }, { offline: `${truck.number} discharging` })
          : await api.post(`/trucks/${truck.id}/status`, { status: "discharging" }, { offline: `${truck.number} discharging` });
        if (!q(res, "discharging", "discharging")) toast.success(`${truck.number} → discharging`);
      } else if (truck.status === "discharging") {
        // DISCHARGING → DELIVERED
        if (docket) {
          const res = await api.post(`/dockets/${docket.id}/status`, { status: "delivered" }, { offline: `${truck.number} delivered` });
          if (!q(res, "returning", "delivered")) {
            const waitCharge = res.data?.wait_charge || 0;
            if (waitCharge > 0) {
              toast.success(`${truck.number} delivered · +$${waitCharge.toFixed(2)} wait charge (${res.data.wait_minutes_chargeable}m over grace)`);
            } else {
              toast.success(`${truck.number} delivered`);
            }
          }
        } else {
          const res = await api.post(`/trucks/${truck.id}/status`, { status: "returning" }, { offline: `${truck.number} delivered` });
          if (!q(res, "returning")) toast.success(`${truck.number} delivered · returning`);
        }
      } else if (truck.status === "returning") {
        const res = await api.post(`/trucks/${truck.id}/status`, { status: "available", current_job_id: null }, { offline: `${truck.number} back in yard` });
        if (!q(res, "available")) toast.success(`${truck.number} back in yard`);
      }
      if (queuedTruckStatus) {
        setTrucks((prev) => prev.map((t) => (t.id === truck.id ? { ...t, status: queuedTruckStatus, ...(queuedTruckStatus === "available" ? { current_job_id: null } : {}) } : t)));
        if (docket && queuedDocketStatus) setDockets((prev) => prev.map((d) => (d.id === docket.id ? { ...d, status: queuedDocketStatus } : d)));
        return;
      }
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const [shareJob, setShareJob] = useState(null);
  const [cloneTarget, setCloneTarget] = useState(null);   // job being cloned → date picker
  const [cloneDate, setCloneDate] = useState(localToday());
  const [cloning, setCloning] = useState(false);
  const openClone = (j) => { setCloneTarget(j); setCloneDate(localToday()); };
  const confirmClone = async () => {
    if (!cloneTarget) return;
    setCloning(true);
    const res = await cloneJob(cloneTarget, cloneDate);
    setCloning(false);
    if (res) setCloneTarget(null);
  };
  const createJob = async () => {
    if (form.is_cash_sale) {
      if (!form.walk_in_name.trim()) return toast.error("Walk-in customer name required");
      if (!form.site_address.trim()) return toast.error("Site address required for EFT-sale jobs");
    } else {
      if (!form.customer_id) return toast.error("Customer required");
      if (form.use_custom_site && !form.site_address.trim()) return toast.error("Site address required");
      if (!form.use_custom_site && !form.site_id && (customer?.sites?.length || 0) > 0) return toast.error("Select a site or use a custom address");
    }
    if (!fleet && !form.mix_id) return toast.error("Mix required");
    if (fleet && !form.material_id && !(form.product || "").trim()) return toast.error("Product / material required");
    if (!form.date) return toast.error("Date required");
    // Validate extra mix lines
    const validExtras = (form.extra_lines || []).filter((l) => l.mix_id && Number(l.quantity_m3) > 0);
    for (const l of (form.extra_lines || [])) {
      if ((l.mix_id && !(Number(l.quantity_m3) > 0)) || (!l.mix_id && Number(l.quantity_m3) > 0)) {
        return toast.error("Every extra mix line needs both a mix and a quantity");
      }
    }
    try {
      const payload = fleet ? {
        product: (form.product || "").trim(),
        material_id: form.material_id || undefined,
        unit: form.unit || "m3",
        quantity_m3: form.quantity_m3,
        date: form.date,
        time_required: form.time_required,
        po_number: form.po_number,
        price_on_docket: !!form.price_on_docket,
        ordered_by: (form.ordered_by || "").trim(),
        site_contact_phone: (form.site_contact_phone || "").trim(),
        notes: form.notes,
        notes_to_driver: !!form.notes_to_driver,
        plant_id: plantId,
      } : {
        mix_id: form.mix_id,
        quantity_m3: form.quantity_m3,
        date: form.date,
        time_required: form.time_required,
        po_number: form.po_number,
        price_on_docket: !!form.price_on_docket,
        ordered_by: (form.ordered_by || "").trim(),
        site_contact_phone: (form.site_contact_phone || "").trim(),
        notes: form.notes,
        notes_to_driver: !!form.notes_to_driver,
        extras: form.extras,
        extra_tag_ids: form.extra_tag_ids,
        surcharge_ids: form.surcharge_ids,
        plant_id: plantId,
        mini_load_surcharge: !!form.mini_load_surcharge,
        ex_plant: !!form.ex_plant,
        extra_lines: validExtras.map((l) => ({ mix_id: l.mix_id, quantity_m3: Number(l.quantity_m3) })),
      };
      if (form.is_cash_sale) {
        payload.is_cash_sale = true;
        payload.walk_in_customer = {
          name: form.walk_in_name.trim(),
          phone: form.walk_in_phone.trim() || null,
          email: form.walk_in_email.trim() || null,
        };
        // v3.11 — MYOB payment code stamped on the job so it flows through
        // to the invoice + AccountRight export.
        if ((form.myob_payment_method || "").toUpperCase() === "EFT") {
          payload.myob_payment_method = "EFT";
          payload.payment_method = "eft";
        }
        // Cash sales always use a custom site — never a saved one
        payload.site_name = form.site_name.trim() || "Walk-in delivery";
        payload.site_address = form.site_address.trim();
        payload.save_site = false;
      } else {
        payload.customer_id = form.customer_id;
        if (form.use_custom_site) {
          payload.site_name = form.site_name.trim();
          payload.site_address = form.site_address.trim();
          payload.save_site = form.save_site;
        } else {
          payload.site_id = form.site_id;
        }
      }
      if (form.delivery_km !== "" && form.delivery_km != null && !isNaN(Number(form.delivery_km))) {
        payload.delivery_km = Number(form.delivery_km);
      }
      if (form.trucks_needed !== "" && !isNaN(parseInt(form.trucks_needed, 10))) {
        payload.trucks_needed = parseInt(form.trucks_needed, 10);
      }
      if (form.load_spacing_min !== "" && !isNaN(parseInt(form.load_spacing_min, 10))) {
        payload.load_spacing_min = parseInt(form.load_spacing_min, 10);
      }
      if (form.pump_company_id) {
        payload.pump_company_id = form.pump_company_id;
        if (form.pump_unit_id) payload.pump_unit_id = form.pump_unit_id;
      }
      if (form.price_per_m3 != null && !isNaN(Number(form.price_per_m3))) {
        payload.price_per_m3 = Number(form.price_per_m3);
      }
      await api.post("/jobs", payload);
      toast.success(form.is_cash_sale ? "EFT-sale job created" : "Job created");
      setNewJobOpen(false);
      setForm({ customer_id: "", is_cash_sale: false, myob_payment_method: "", walk_in_name: "", walk_in_phone: "", walk_in_email: "", site_id: "", site_name: "", site_address: "", save_site: true, use_custom_site: false, mix_id: "", material_id: "", unit: "m3", quantity_m3: 8, date: today, time_required: "09:00", po_number: "", ordered_by: "", site_contact_phone: "", notes: "", notes_to_driver: true, extras: "", extra_tag_ids: [], surcharge_ids: [], delivery_km: "", trucks_needed: "", load_spacing_min: "", pump_company_id: "", pump_unit_id: "", mini_load_surcharge: false, ex_plant: false, extra_lines: [] });
      load();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : d?.message || "Failed");
    }
  };

  const cloneJob = async (j, date) => {
    try {
      const qs = date ? `?date=${date}` : "";
      const { data } = await api.post(`/jobs/${j.id}/clone${qs}`);
      toast.success(`Cloned job for ${j.customer_name} — ${date ? formatLocalDate(date) : "today"}`);
      load();
      return data;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Clone failed");
    }
  };

  const openEdit = (j) => {
    setEditJob(j);
    setEditForm({
      date: j.date || today,
      time_required: j.time_required || "",
      quantity_m3: j.quantity_m3,
      po_number: j.po_number || "",
      price_on_docket: !!j.price_on_docket,
      ordered_by: j.ordered_by || "",
      site_contact_phone: j.site_contact_phone || "",
      customer_id: j.customer_id || "",
      site_id: j.site_id || "",
      extras: j.extras || "",
      notes: j.notes || "",
      notes_to_driver: j.notes_to_driver !== false,
      mix_id: j.mix_id || "",
      extra_tag_ids: j.extra_tag_ids || [],
      surcharge_ids: j.surcharge_ids || [],
      delivery_km: j.delivery_km ?? "",
      trucks_needed: j.trucks_needed ?? "",
      load_spacing_min: j.load_spacing_min ?? "",
      pump_company_id: j.pump_company_id ?? "",
      pump_unit_id: j.pump_unit_id ?? "",
      mini_load_surcharge: !!j.mini_load_surcharge,
      ex_plant: !!j.ex_plant,
      lines: (j.lines || []).map((ln) => ({
        id: ln.id,
        mix_id: ln.mix_id,
        quantity_m3: ln.quantity_m3,
        price_per_m3: ln.price_per_m3,
        delivered_m3: ln.delivered_m3 || 0,
      })),
    });
    setRepricing(null);
  };

  const loadRepricing = async () => {
    try {
      const res = await api.get(`/jobs/${editJob.id}/repricing`);
      // Default: pre-select every docket whose billed rate differs from the current rate
      const selected = new Set(res.data.dockets.filter((d) => Math.abs(d.delta) > 0.01).map((d) => d.docket_id));
      setRepricing({ ...res.data, selected });
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to load repricing"); }
  };

  const toggleRepriceRow = (did) => {
    const s = new Set(repricing.selected);
    if (s.has(did)) s.delete(did); else s.add(did);
    setRepricing({ ...repricing, selected: s });
  };

  const applyRepricing = async () => {
    const ids = Array.from(repricing.selected);
    if (ids.length === 0) return toast.error("Select at least one docket");
    try {
      const res = await api.post(`/jobs/${editJob.id}/repricing`, { docket_ids: ids });
      toast.success(`Repriced ${res.data.updated} docket${res.data.updated === 1 ? "" : "s"} @ $${res.data.new_rate.toFixed(2)}/m³`);
      setRepricing(null);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to apply repricing"); }
  };
  // After a price change, offer a one-tap reprice of delivered dockets still on the old rate.
  const offerReprice = async (jobId) => {
    try {
      const r = await api.get(`/jobs/${jobId}/repricing`);
      const mism = (r.data.dockets || []).filter((d) => Math.abs(d.delta) > 0.01 && d.status !== "cancelled");
      if (mism.length === 0) return;
      toast(`${mism.length} delivered load${mism.length === 1 ? "" : "s"} still at the old price`, {
        description: `Reprice to $${r.data.current_rate.toFixed(2)}/m³ to match this order?`,
        duration: 12000,
        action: {
          label: "Reprice",
          onClick: async () => {
            try {
              const res = await api.post(`/jobs/${jobId}/repricing`, { docket_ids: mism.map((d) => d.docket_id) });
              toast.success(`Repriced ${res.data.updated} load${res.data.updated === 1 ? "" : "s"} @ $${res.data.new_rate.toFixed(2)}/m³`);
              load();
            } catch (e) { toast.error(e.response?.data?.detail || "Reprice failed"); }
          },
        },
      });
    } catch { /* non-blocking */ }
  };
  const saveEdit = async () => {
    const jobId = editJob.id;
    const newPrimaryPrice = (Array.isArray(editForm.lines) && editForm.lines[0] && editForm.lines[0].price_per_m3 != null)
      ? Number(editForm.lines[0].price_per_m3) : null;
    const priceChanged = newPrimaryPrice != null && Number(editJob.price_per_m3) !== newPrimaryPrice;
    try {
      const payload = { ...editForm };
      // Only send delivery_km when it's a valid number; blanking the field means "no change"
      if (payload.delivery_km === "" || payload.delivery_km == null || isNaN(Number(payload.delivery_km))) {
        delete payload.delivery_km;
      } else {
        payload.delivery_km = Number(payload.delivery_km);
      }
      // Truck-spacing fields — omit when blank so backend leaves them unchanged
      if (payload.trucks_needed === "" || payload.trucks_needed == null || isNaN(parseInt(payload.trucks_needed, 10))) {
        delete payload.trucks_needed;
      } else {
        payload.trucks_needed = parseInt(payload.trucks_needed, 10);
      }
      if (payload.load_spacing_min === "" || payload.load_spacing_min == null || isNaN(parseInt(payload.load_spacing_min, 10))) {
        delete payload.load_spacing_min;
      } else {
        payload.load_spacing_min = parseInt(payload.load_spacing_min, 10);
      }
      // Pump company: "" means no pump (send null to clear), otherwise send the id
      if (payload.pump_company_id === "" || payload.pump_company_id == null) {
        payload.pump_company_id = null;
        payload.pump_unit_id = "";
      } else {
        payload.pump_unit_id = payload.pump_unit_id || "";
      }
      // v3.4h — customer_id: only send if it actually changed (avoid needless cascade)
      if (payload.customer_id === "" || payload.customer_id == null || payload.customer_id === editJob.customer_id) {
        delete payload.customer_id;
      }
      // Multi-line: send lines[] and strip legacy single-mix fields the backend
      // will re-derive from the primary line.
      if (Array.isArray(payload.lines) && payload.lines.length > 0) {
        const cleanLines = payload.lines.filter((l) => l.mix_id && Number(l.quantity_m3) > 0);
        if (cleanLines.length === 0) return toast.error("At least one mix line required");
        for (const l of payload.lines) {
          if ((l.mix_id && !(Number(l.quantity_m3) > 0)) || (!l.mix_id && Number(l.quantity_m3) > 0)) {
            return toast.error("Every mix line needs both a mix and a quantity");
          }
        }
        payload.lines = cleanLines.map((l) => ({ mix_id: l.mix_id, quantity_m3: Number(l.quantity_m3), price_per_m3: l.price_per_m3 ?? null }));
        delete payload.mix_id;
        delete payload.quantity_m3;
      } else {
        delete payload.lines;
      }
      delete payload.delivered_m3;
      // Site: only send when the dispatcher actually picked a different saved site — an
      // empty/unchanged value must not wipe an ad-hoc address typed on the job.
      if (!payload.site_id || payload.site_id === (editJob.site_id || "")) delete payload.site_id;
      const res = await api.patch(`/jobs/${editJob.id}`, payload, { offline: `Edit order · ${editJob.customer_name || ""}` });
      if (res.data?.queued) {
        setDash((prev) => (prev ? { ...prev, jobs: (prev.jobs || []).map((j) => (j.id === editJob.id ? { ...j, ...payload, lines: j.lines } : j)) } : prev));
        setEditJob(null);
        return;
      }
      toast.success("Order updated");
      setEditJob(null);
      load();
      if (priceChanged) offerReprice(jobId);
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };
  const closeJob = async (j) => {
    try {
      await api.patch(`/jobs/${j.id}`, { status: "completed" });
      toast.success(`Order ${j.customer_name} closed`);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };
  const cancelJob = async (j) => {
    try {
      await api.patch(`/jobs/${j.id}`, { status: "cancelled" });
      toast.success(`Order for ${j.customer_name || "customer"} cancelled`, {
        duration: 8000,
        action: { label: "Undo", onClick: () => undoCancelJob(j) },
      });
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to cancel"); }
  };
  const undoCancelJob = async (j) => {
    try {
      await api.patch(`/jobs/${j.id}`, { status: "scheduled" });
      toast.success(`Order ${j.customer_name || ""} restored`);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to restore"); }
  };
  const reopenJob = async (j) => {
    try {
      await api.patch(`/jobs/${j.id}`, { status: "scheduled" });
      toast.success("Order re-opened");
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  // v3.2 — One-tap SMS tracking link.
  // Opens a compose dialog (works on desktop + mobile) pre-filled with the
  // customer's number, tracking URL, and a QR code. Users can Copy the message
  // or, on mobile, tap "Open SMS app" to fire the native SMS composer.
  const [wxRisk, setWxRisk] = useState(false);
  useEffect(() => {
    const pull = () => api.get("/weather").then((r) => setWxRisk(!!r.data?.has_risk)).catch(() => {});
    pull();
    const t = setInterval(pull, 10 * 60 * 1000);
    return () => clearInterval(t);
  }, []);
  const textConditions = async (j) => {
    const note = window.prompt(`Text ${j.customer_name} about today's pour conditions?\nThey reply 1 = go ahead, 2 = delay, 3 = call me.\nOptional extra line:`, "");
    if (note === null) return;
    try {
      const { data } = await api.post(`/jobs/${j.id}/weather-alert`, { note });
      toast.success(`Conditions text sent to ${data.to}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not send"); }
  };
  const smsTrackingLink = async (j) => {
    const cust = customers.find((c) => c.id === j.customer_id);
    const phone = (cust?.phone || "").trim();
    let url = "";
    try {
      const r = await api.post(`/customers/${j.customer_id}/ensure-track-token`);
      url = `${window.location.origin}/track/${r.data.track_token}`;
    } catch (err) {
      toast.error("Could not create tracking link");
      return;
    }
    const timeBit = j.time_required ? ` at ${j.time_required}` : "";
    const siteBit = (j.site_address || j.site_name) ? ` to ${j.site_address || j.site_name}` : "";
    const message = `Hi ${j.customer_name}, your ${org?.name || "ArmorBatch"} delivery${siteBit}${timeBit} is scheduled. Track live: ${url}`;
    setSmsCompose({
      phone,
      message,
      trackingUrl: url,
      customerName: j.customer_name,
    });
  };

  if (!dash) return <div className="p-8 label-tag text-primary-orange pulse-orange">Loading ArmorBatch Global…</div>;

  const jobs = dash.jobs || [];
  const openJobs = jobs.filter((j) => j.status !== "completed" && j.status !== "cancelled").sort((a, b) => (a.time_required || "").localeCompare(b.time_required || ""));
  const closedJobs = jobs.filter((j) => j.status === "completed").sort((a, b) => (b.completed_at || "").localeCompare(a.completed_at || ""));
  const cancelledJobs = jobs.filter((j) => j.status === "cancelled").sort((a, b) => (b.cancelled_at || "").localeCompare(a.cancelled_at || ""));
  const orderedJobs = [...openJobs, ...closedJobs];
  const availableTrucks = trucks.filter((t) => t.status === "available");
  const customer = customers.find((c) => c.id === form.customer_id);
  const editCustomer = editJob ? customers.find((c) => c.id === editJob.customer_id) : null;

  // Batch state per (job, truck) — but only show as "current" if it is actively in flight.
  // Historical completed batches shouldn't block a fresh Start Load when the truck is
  // dragged again onto the same job.
  const batchByKey = {};
  for (const b of batches) {
    const key = `${b.job_id}::${b.truck_id}`;
    const cur = batchByKey[key];
    if (!cur) { batchByKey[key] = b; continue; }
    const rank = (s) => (s === "batching" ? 3 : s === "queued" ? 2 : s === "complete" ? 1 : 0);
    if (rank(b.status) > rank(cur.status)) batchByKey[key] = b;
  }
  const docketByBatch = {};
  for (const d of dockets) {
    if (d.batch_id) docketByBatch[d.batch_id] = d;
  }
  Object.values(batchByKey).forEach((b) => {
    const dk = docketByBatch[b.id];
    if (dk) {
      b.docket_id = dk.id;
      b.docket_no = dk.docket_no;
    }
  });
  // Filter: current batch is only shown to the chip when active OR truck is still
  // in flight (en_route / on_site). Once truck is available again, the batch is history.
  const currentBatchFor = (jobId, truck) => {
    const b = batchByKey[`${jobId}::${truck.id}`];
    if (!b) return null;
    if (b.status === "queued" || b.status === "batching") return b;
    // complete: only relevant while the truck is still delivering this specific load
    if (b.status === "complete" && (truck.status === "en_route" || truck.status === "on_site")) return b;
    return null;
  };

  return (
    <div className="p-2 lg:p-3 flex flex-col h-full gap-2" data-testid="command-page">
      <WeatherReplyWatcher />
      {/* ─── Toolbar ────────────────────────────────────────────────── */}
      <div className="card-industrial px-3 py-1.5 flex items-center gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <div>
            <div className="label-tag flex items-center gap-2 text-[9px]">
              PLANT COMMAND
              <span
                data-testid="ws-status-pill"
                className={`inline-flex items-center gap-1 px-1 py-0 border mono text-[8px] uppercase tracking-widest ${
                  liveStatus === "live" ? "border-primary-orange text-primary-orange" :
                  liveStatus === "connecting" ? "border-industrial text-muted-industrial" :
                  "border-red-700 text-red-400"
                }`}
              >
                <Radio className={`w-2 h-2 ${liveStatus === "live" ? "pulse-orange" : ""}`} />
                {liveStatus === "live" ? "LIVE" : liveStatus === "connecting" ? "CONNECTING" : "OFFLINE"}
              </span>
            </div>
            <h1 className="heading text-base font-black uppercase tracking-tight leading-none mt-0.5">
              {viewDate === today ? "Today" : viewDate < today ? "Past" : "Upcoming"} · <span className="text-primary-orange mono">{viewDate}</span>
            </h1>
          </div>
        </div>

        <button data-testid="command-war-room-toggle" onClick={() => nav("/war-room")} title="Switch to the 3-column War Room"
          className="mono text-[10px] uppercase tracking-widest px-2 py-1 border border-industrial text-ink hover:border-primary-orange hover:text-primary-orange">
          War Room
        </button>

        <div className="flex items-center gap-1 border border-industrial px-1 py-0.5 bg-surface">
          <button data-testid="date-prev" onClick={() => shiftDate(-1)} className="text-muted-industrial hover:text-primary-orange px-1.5 py-0.5 mono text-xs">‹</button>
          <CalIcon className="w-3 h-3 text-primary-orange" />
          <Input type="date" data-testid="date-picker" value={viewDate} onChange={(e) => setViewDate(e.target.value)} className="rounded-none bg-transparent border-0 mono h-6 px-1 w-[125px] text-[11px] focus-visible:ring-0" />
          <button data-testid="date-next" onClick={() => shiftDate(1)} className="text-muted-industrial hover:text-primary-orange px-1.5 py-0.5 mono text-xs">›</button>
        </div>

        <button data-testid="date-today" onClick={goToday}
          className={`mono text-[10px] uppercase tracking-widest px-2 py-1 border ${viewDate === today ? "border-primary-orange text-primary-orange bg-primary-orange/10" : "border-industrial text-ink hover:border-primary-orange"}`}>
          Today
        </button>
        <button data-testid="date-tomorrow" onClick={goTomorrow}
          className={`mono text-[10px] uppercase tracking-widest px-2 py-1 border ${viewDate === shiftLocalDate(today, 1) ? "border-primary-orange text-primary-orange bg-primary-orange/10" : "border-industrial text-ink hover:border-primary-orange"}`}>
          Tomorrow
        </button>
        <button data-testid="refresh-btn" onClick={() => load()}
          className="mono text-[10px] uppercase tracking-widest px-2 py-1 border border-industrial text-ink hover:border-primary-orange inline-flex items-center gap-1">
          <RefreshCcw className="w-3 h-3" /> Refresh
        </button>
        <PrintScheduleButton plantId={plantId} />
        <PriceCalcPanel />
        <button
          data-testid="focus-mode-toggle"
          onClick={() => setFocusMode((v) => !v)}
          title={focusMode ? "Exit focus mode — restore KPI strip" : "Focus mode — hide KPI strip, maximise job list"}
          className={`mono text-[10px] uppercase tracking-widest px-2 py-1 border inline-flex items-center gap-1 ${focusMode ? "border-primary-orange text-primary-orange bg-primary-orange/10" : "border-industrial text-muted-industrial hover:border-primary-orange"}`}
        >
          {focusMode ? "● FOCUS" : "○ FOCUS"}
        </button>

        {/* KPIs — hide the 4 KPI cells in focus mode to reclaim height. */}
        <div className="flex items-center gap-3 ml-auto flex-wrap">
          {!focusMode && (
            <>
              <KpiCell icon={Factory} label="Production" value={`${(dash.production_m3 ?? 0).toFixed(1)} / ${(dash.booked_m3 ?? 0).toFixed(1)}`} sub="Delivered / Booked m³" />
              <KpiCell icon={Truck} label="Trucks" value={`${dash.active_trucks}/${dash.total_trucks}`} sub="Active" />
              <KpiCell icon={ClipboardList} label="Remaining" value={dash.jobs_remaining} sub={`of ${dash.jobs_today}`} />
              <KpiCell icon={AlertTriangle} label="Alerts" value={dash.inventory_alerts.length} sub="Low stock" />
            </>
          )}

          <Dialog open={newJobOpen} onOpenChange={setNewJobOpen}>
            <DialogTrigger asChild>
              <Button data-testid="new-job-btn" className="rounded-none bg-primary-orange hover:bg-[rgb(var(--primary-hover))] uppercase tracking-widest text-xs font-bold h-9">
                <Plus className="w-4 h-4 mr-1" /> New Job
              </Button>
            </DialogTrigger>
            <DialogContent className="rounded-none bg-surface border-industrial max-w-lg max-h-[85vh] flex flex-col p-0" data-testid="job-dialog">
              <DialogHeader className="px-6 pt-6 pb-3 shrink-0 border-b border-industrial"><DialogTitle className="heading uppercase">New Job</DialogTitle></DialogHeader>
              <div className="space-y-3 overflow-y-auto flex-1 px-6 py-4">
                {/* v3.11 · EFT sale (walk-in) toggle — was "Cash sale". */}
                <div className="flex items-center justify-between border border-industrial p-2 bg-surface-2/40">
                  <div>
                    <div className="label-tag">EFT Sale · Walk-in</div>
                    <div className="text-[10px] text-muted-industrial">Toggle ON if the customer is a walk-in / does not have an account.</div>
                  </div>
                  <button
                    type="button"
                    data-testid="toggle-cash-sale"
                    onClick={() => setForm({ ...form, is_cash_sale: !form.is_cash_sale, customer_id: "", site_id: "", walk_in_name: form.is_cash_sale ? "" : form.walk_in_name, myob_payment_method: !form.is_cash_sale ? "EFT" : "" })}
                    className={`mono text-[10px] uppercase tracking-widest px-3 py-1 border transition-colors ${form.is_cash_sale ? "border-primary-orange text-primary-orange bg-primary-orange/10" : "border-industrial text-muted-industrial hover:border-primary-orange hover:text-primary-orange"}`}
                  >
                    {form.is_cash_sale ? "● EFT sale" : "○ Account"}
                  </button>
                </div>

                {form.is_cash_sale && (
                  <label
                    className="flex items-start gap-2 cursor-pointer border border-industrial p-2 bg-surface-2/40 hover:border-primary-orange transition"
                    data-testid="myob-eft-label"
                  >
                    <input
                      type="checkbox"
                      data-testid="myob-eft-checkbox"
                      checked={(form.myob_payment_method || "").toUpperCase() === "EFT"}
                      onChange={(e) => setForm({ ...form, myob_payment_method: e.target.checked ? "EFT" : "" })}
                      className="mt-0.5"
                    />
                    <div className="flex-1">
                      <div className="mono text-[10px] uppercase tracking-widest text-ink">Link MYOB code · <span className="text-primary-orange">EFT</span></div>
                      <div className="mono text-[9px] text-muted-industrial mt-0.5">
                        Stamps the invoice's Payment Method column as <b>EFT</b> when exported to MYOB AccountRight — so bank rec lines up cleanly.
                      </div>
                    </div>
                  </label>
                )}

                {/* v3.16 · Requested Mini Load — 10% surcharge on every docket total */}
                <label
                  className="flex items-start gap-2 cursor-pointer border border-industrial p-2 bg-surface-2/40 hover:border-primary-orange transition"
                  data-testid="mini-load-label"
                >
                  <input
                    type="checkbox"
                    data-testid="mini-load-checkbox"
                    checked={!!form.mini_load_surcharge}
                    onChange={(e) => setForm({ ...form, mini_load_surcharge: e.target.checked })}
                    className="mt-0.5"
                  />
                  <div className="flex-1">
                    <div className="mono text-[10px] uppercase tracking-widest text-ink">Requested Mini Load · <span className="text-primary-orange">+10%</span></div>
                    <div className="mono text-[9px] text-muted-industrial mt-0.5">
                      Adds a 10% "Requested Mini Load Surcharge" line to every docket + invoice generated for this order.
                    </div>
                  </div>
                </label>

                {/* Ex-plant pickup — customer collects at the plant, so no cartage/fuel surcharge */}
                <label
                  className="flex items-start gap-2 cursor-pointer border border-industrial p-2 bg-surface-2/40 hover:border-primary-orange transition"
                  data-testid="ex-plant-label"
                >
                  <input
                    type="checkbox"
                    data-testid="ex-plant-checkbox"
                    checked={!!form.ex_plant}
                    onChange={(e) => setForm({ ...form, ex_plant: e.target.checked })}
                    className="mt-0.5"
                  />
                  <div className="flex-1">
                    <div className="mono text-[10px] uppercase tracking-widest text-ink">Ex-plant · <span className="text-primary-orange">picked up at plant</span></div>
                    <div className="mono text-[9px] text-muted-industrial mt-0.5">
                      Customer collects at the plant — removes all cartage/delivery and the fuel surcharge. Prints "EX-PLANT" on the docket.
                    </div>
                  </div>
                </label>

                {!form.is_cash_sale ? (
                  <div>
                    <Label className="label-tag">Customer</Label>
                    <Select value={form.customer_id} onValueChange={(v) => setForm({ ...form, customer_id: v, site_id: "" })}>
                      <SelectTrigger data-testid="job-customer" className="rounded-none bg-transparent border-industrial"><SelectValue placeholder="Select customer" /></SelectTrigger>
                      <SelectContent className="rounded-none bg-surface border-industrial">
                        {customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                ) : (
                  <div className="space-y-2 border-l-2 border-primary-orange pl-3">
                    <div>
                      <Label className="label-tag">Walk-in customer name *</Label>
                      <Input data-testid="job-walkin-name" value={form.walk_in_name} onChange={(e) => setForm({ ...form, walk_in_name: e.target.value })} placeholder="e.g. Jane Smith · ABC Landscaping" className="rounded-none bg-transparent border-industrial mono" />
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <Input data-testid="job-walkin-phone" value={form.walk_in_phone} onChange={(e) => setForm({ ...form, walk_in_phone: e.target.value })} placeholder="Phone" className="rounded-none bg-transparent border-industrial mono" />
                      <Input data-testid="job-walkin-email" value={form.walk_in_email} onChange={(e) => setForm({ ...form, walk_in_email: e.target.value })} placeholder="Email (optional)" className="rounded-none bg-transparent border-industrial mono" />
                    </div>
                  </div>
                )}
                {form.is_cash_sale && (
                  <div>
                    <Label className="label-tag">Delivery site *</Label>
                    <div className="space-y-2 border border-industrial p-2 bg-surface-2/40">
                      <Input
                        data-testid="cash-site-name"
                        value={form.site_name}
                        onChange={(e) => setForm({ ...form, site_name: e.target.value })}
                        placeholder="Site label (optional)"
                        className="rounded-none bg-transparent border-industrial mono h-9 text-sm"
                      />
                      <Input
                        data-testid="cash-site-address"
                        value={form.site_address}
                        onChange={(e) => setForm({ ...form, site_address: e.target.value })}
                        placeholder="Full address · e.g. 12 Example St, Suburb TAS 7000"
                        className="rounded-none bg-transparent border-industrial mono h-9 text-sm"
                      />
                    </div>
                  </div>
                )}
                {customer && (
                  <div>
                    <div className="flex items-center justify-between mb-1.5">
                      <Label className="label-tag">Site</Label>
                      <button
                        type="button"
                        data-testid="toggle-custom-site"
                        onClick={() => setForm({ ...form, use_custom_site: !form.use_custom_site, site_id: "", site_name: "", site_address: "" })}
                        className={`mono text-[10px] uppercase tracking-widest px-2 py-0.5 border ${form.use_custom_site ? "border-primary-orange text-primary-orange bg-primary-orange/10" : "border-industrial text-muted-industrial hover:border-primary-orange hover:text-primary-orange"}`}
                      >
                        {form.use_custom_site ? "✓ New Address" : "+ New Address"}
                      </button>
                    </div>
                    {!form.use_custom_site ? (
                      customer.sites?.length > 0 ? (
                        <Select value={form.site_id} onValueChange={(v) => setForm({ ...form, site_id: v })}>
                          <SelectTrigger data-testid="job-site" className="rounded-none bg-transparent border-industrial"><SelectValue placeholder="Select site" /></SelectTrigger>
                          <SelectContent className="rounded-none bg-surface border-industrial">
                            {customer.sites.map((s) => <SelectItem key={s.id} value={s.id}>{s.name} — {s.address}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      ) : (
                        <div className="text-[11px] text-muted-industrial border border-dashed border-industrial p-2">
                          No saved sites — tap <span className="text-primary-orange mono">+ NEW ADDRESS</span> above to enter one.
                        </div>
                      )
                    ) : (
                      <div className="space-y-2 border border-industrial p-2 bg-surface-2/40">
                        <Input
                          data-testid="job-site-name"
                          value={form.site_name}
                          onChange={(e) => setForm({ ...form, site_name: e.target.value })}
                          placeholder="Site label (e.g. Slab pour · Lot 4)"
                          className="rounded-none bg-transparent border-industrial mono h-9 text-sm"
                        />
                        <Input
                          data-testid="job-site-address"
                          value={form.site_address}
                          onChange={(e) => setForm({ ...form, site_address: e.target.value })}
                          placeholder="Full address · e.g. 12 Example St, Suburb TAS 7000"
                          className="rounded-none bg-transparent border-industrial mono h-9 text-sm"
                        />
                        <label className="flex items-center gap-2 text-[11px] mono uppercase tracking-widest text-muted-industrial cursor-pointer">
                          <input
                            type="checkbox"
                            data-testid="job-save-site"
                            checked={form.save_site}
                            onChange={(e) => setForm({ ...form, save_site: e.target.checked })}
                            className="w-3.5 h-3.5 accent-[rgb(var(--primary))]"
                          />
                          Save to {customer.name} for future jobs
                        </label>
                      </div>
                    )}
                  </div>
                )}
                {fleet ? (
                  <div className="space-y-2 border border-industrial p-3" data-testid="fleet-material-block">
                    <Label className="label-tag">Product / Material</Label>
                    <div className="grid grid-cols-[1fr_90px_90px] gap-2 items-end">
                      {materials.length > 0 ? (
                        <Select value={form.material_id} onValueChange={(v) => {
                          const m = materials.find((x) => x.id === v);
                          setForm({ ...form, material_id: v, mix_id: "", unit: m ? (m.unit || "m3") : form.unit });
                        }}>
                          <SelectTrigger data-testid="job-material" className="rounded-none bg-transparent border-industrial"><SelectValue placeholder="Select material" /></SelectTrigger>
                          <SelectContent className="rounded-none bg-surface border-industrial">
                            {materials.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      ) : (
                        <Input data-testid="job-material-text" value={form.mix_id === "" ? (form.product || "") : ""} onChange={(e) => setForm({ ...form, product: e.target.value, material_id: "" })} placeholder="e.g. Pine bark mulch" className="rounded-none bg-transparent border-industrial mono" />
                      )}
                      <Input data-testid="job-qty" type="number" step="0.5" value={form.quantity_m3} onChange={(e) => setForm({ ...form, quantity_m3: parseFloat(e.target.value) })} className="rounded-none bg-transparent border-industrial mono" placeholder="qty" />
                      <select data-testid="job-unit" value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} className="h-10 rounded-none bg-transparent border border-industrial mono text-sm px-2">
                        {FLEET_UNITS.map((u) => <option key={u.value} value={u.value}>{u.label}</option>)}
                      </select>
                    </div>
                    {materials.length === 0 && <div className="mono text-[10px] uppercase tracking-widest text-muted-industrial">Add your products in <a href="/materials" className="text-primary-orange">Materials</a> for a quick pick list</div>}
                  </div>
                ) : (
                <div className="space-y-2 border border-industrial p-3">
                  <div className="flex items-center justify-between">
                    <Label className="label-tag">Mix Lines</Label>
                    <Button
                      type="button"
                      variant="ghost"
                      onClick={() => setForm({ ...form, extra_lines: [...(form.extra_lines || []), { mix_id: "", quantity_m3: 3 }] })}
                      data-testid="add-mix-line"
                      className="h-6 px-2 mono uppercase text-[10px] tracking-widest text-primary-orange hover:text-primary-orange"
                    >
                      + Add mix
                    </Button>
                  </div>
                  <div className="grid grid-cols-[1fr_100px] gap-2 items-end">
                    <Select value={form.mix_id} onValueChange={(v) => setForm({ ...form, mix_id: v, price_per_m3: null })}>
                      <SelectTrigger data-testid="job-mix" className="rounded-none bg-transparent border-industrial"><SelectValue placeholder="Select mix" /></SelectTrigger>
                      <SelectContent className="rounded-none bg-surface border-industrial">
                        {sortMixes(mixes).map((m) => <SelectItem key={m.id} value={m.id}>{m.code} — {m.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <Input
                      data-testid="job-qty"
                      type="number"
                      step="0.2"
                      value={form.quantity_m3}
                      onChange={(e) => setForm({ ...form, quantity_m3: parseFloat(e.target.value) })}
                      className="rounded-none bg-transparent border-industrial mono"
                      placeholder="m³"
                    />
                  </div>
                  {(form.extra_lines || []).map((line, i) => (
                    <div key={i} className="grid grid-cols-[1fr_100px_30px] gap-2 items-end" data-testid={`extra-mix-line-${i}`}>
                      <Select value={line.mix_id} onValueChange={(v) => {
                        const arr = [...form.extra_lines]; arr[i] = { ...arr[i], mix_id: v }; setForm({ ...form, extra_lines: arr });
                      }}>
                        <SelectTrigger className="rounded-none bg-transparent border-industrial"><SelectValue placeholder="Extra mix" /></SelectTrigger>
                        <SelectContent className="rounded-none bg-surface border-industrial">
                          {sortMixes(mixes).map((m) => <SelectItem key={m.id} value={m.id}>{m.code} — {m.name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                      <Input
                        type="number"
                        step="0.5"
                        value={line.quantity_m3}
                        onChange={(e) => {
                          const arr = [...form.extra_lines]; arr[i] = { ...arr[i], quantity_m3: parseFloat(e.target.value) || 0 }; setForm({ ...form, extra_lines: arr });
                        }}
                        className="rounded-none bg-transparent border-industrial mono"
                        placeholder="m³"
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        onClick={() => setForm({ ...form, extra_lines: form.extra_lines.filter((_, j) => j !== i) })}
                        className="h-8 w-8 p-0 text-red-500 hover:text-red-400"
                        data-testid={`remove-mix-line-${i}`}
                      >
                        ×
                      </Button>
                    </div>
                  ))}
                  {(form.extra_lines || []).length > 0 && (
                    <div className="mono text-[10px] uppercase tracking-widest text-muted-industrial">
                      Order total: {(Number(form.quantity_m3 || 0) + (form.extra_lines || []).reduce((s, l) => s + Number(l.quantity_m3 || 0), 0)).toFixed(1)} m³
                    </div>
                  )}
                </div>
                )}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="label-tag">Date</Label>
                    <Input data-testid="job-date" type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="rounded-none bg-transparent border-industrial mono" />
                  </div>
                  <div>
                    <Label className="label-tag">Time</Label>
                    <Input data-testid="job-time" type="time" value={form.time_required} onChange={(e) => setForm({ ...form, time_required: e.target.value })} className="rounded-none bg-transparent border-industrial mono" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="label-tag">PO Number</Label>
                    <Input data-testid="job-po" value={form.po_number} onChange={(e) => setForm({ ...form, po_number: e.target.value })} className="rounded-none bg-transparent border-industrial mono" />
                  </div>
                  <div>
                    <Label className="label-tag">Ordered By {form.is_cash_sale ? "" : <span className="text-muted-industrial normal-case font-normal">· person on site</span>}</Label>
                    <Input data-testid="job-ordered-by" placeholder="Name of person who called" value={form.ordered_by || ""} onChange={(e) => setForm({ ...form, ordered_by: e.target.value })} className="rounded-none bg-transparent border-industrial mono" />
                  </div>
                  <label className="col-span-2 flex items-center gap-2 cursor-pointer select-none" data-testid="job-price-on-docket">
                    <input type="checkbox" checked={!!form.price_on_docket} onChange={(e) => setForm({ ...form, price_on_docket: e.target.checked })} className="accent-[#F97316] w-4 h-4" />
                    <span className="mono text-[10px] uppercase tracking-widest text-ink inline-flex items-center gap-1"><Receipt className="w-3 h-3" /> Show price on docket</span>
                    <span className="mono text-[9px] text-muted-industrial uppercase">(hidden by default)</span>
                  </label>
                </div>
                <div>
                  <Label className="label-tag">Site contact mobile <span className="text-muted-industrial normal-case font-normal">· gets the "truck has left" text + driver can call</span></Label>
                  <Input data-testid="job-site-contact-phone" type="tel" inputMode="tel" placeholder="04xx xxx xxx" value={form.site_contact_phone || ""} onChange={(e) => setForm({ ...form, site_contact_phone: e.target.value })} className="rounded-none bg-transparent border-industrial mono" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="label-tag">Trucks needed</Label>
                    <Input
                      data-testid="job-trucks-needed"
                      type="number"
                      min="1"
                      step="1"
                      value={form.trucks_needed ?? ""}
                      onChange={(e) => setForm({ ...form, trucks_needed: e.target.value })}
                      placeholder="e.g. 5"
                      className="rounded-none bg-transparent border-industrial mono"
                    />
                  </div>
                  <div>
                    <Label className="label-tag">Load spacing (min)</Label>
                    <Input
                      data-testid="job-load-spacing"
                      type="number"
                      min="0"
                      step="5"
                      value={form.load_spacing_min ?? ""}
                      onChange={(e) => setForm({ ...form, load_spacing_min: e.target.value })}
                      placeholder="e.g. 20"
                      className="rounded-none bg-transparent border-industrial mono"
                    />
                  </div>
                </div>
                {form.time_required && form.load_spacing_min && form.trucks_needed && (
                  <div className="mono text-[10px] uppercase tracking-widest text-primary-orange border border-primary-orange/40 bg-primary-orange/5 px-2 py-1.5">
                    Schedule: {computeLoadSlots(form.time_required, parseInt(form.trucks_needed, 10), parseInt(form.load_spacing_min, 10)).join(" · ")}
                  </div>
                )}
                {!fleet && (
                <div>
                  <Label className="label-tag">Pump Company (optional)</Label>
                  <select
                    data-testid="job-pump-company"
                    value={form.pump_company_id ?? ""}
                    onChange={(e) => setForm({ ...form, pump_company_id: e.target.value, pump_unit_id: "" })}
                    className="w-full mt-1 rounded-none bg-transparent border border-industrial mono text-sm p-2"
                  >
                    <option value="">— No pump / owner-placed —</option>
                    {pumpCompanies.map((pc) => (
                      <option key={pc.id} value={pc.id}>{pc.name}{pc.phone ? ` · ` : ""}{pc.partner_id ? "" : " · not on partner network"}</option>
                    ))}
                  </select>
                  <div className="text-[10px] mono uppercase tracking-widest text-muted-industrial mt-1">
                    Assign a pump crew so their QR tracker shows this job today.
                  </div>
                  <PumpUnitSelect companyId={form.pump_company_id} value={form.pump_unit_id}
                    onChange={(v) => setForm({ ...form, pump_unit_id: v })} testId="job-pump-unit" />
                </div>
                )}
                <div>
                  <Label className="label-tag">Distance from plant (km)</Label>
                  <Input
                    data-testid="job-distance"
                    type="number"
                    step="0.1"
                    min="0"
                    value={form.delivery_km ?? ""}
                    onChange={(e) => setForm({ ...form, delivery_km: e.target.value === "" ? "" : parseFloat(e.target.value) })}
                    placeholder="Leave blank to auto-detect from address"
                    className="rounded-none bg-transparent border-industrial mono"
                  />
                  {(form.delivery_km === "" || form.delivery_km == null || isNaN(Number(form.delivery_km))) && (previewKm.loading || previewKm.km != null) && (
                    <div className="mt-1.5 mono text-[10px] uppercase tracking-widest text-primary-orange" data-testid="km-preview">
                      {previewKm.loading
                        ? "Calculating driving distance…"
                        : previewKm.km == null
                          ? ""
                          : `Auto: ${Number(previewKm.km).toFixed(1)} km · ${previewKm.fee > 0 ? `$${Number(previewKm.fee).toFixed(2)} delivery fee` : "in free 25km zone"}`}
                    </div>
                  )}
                  {form.delivery_km !== "" && form.delivery_km != null && !isNaN(Number(form.delivery_km)) && (() => {
                    const km = Number(form.delivery_km);
                    const over = Math.max(0, km - 25);
                    const fee = over * 2;
                    return (
                      <div className={`mt-1.5 mono text-[10px] uppercase tracking-widest ${over > 0 ? "text-primary-orange" : "text-emerald-500"}`}>
                        {over > 0
                          ? `Manual +${over.toFixed(1)}km over · Delivery fee $${fee.toFixed(2)}`
                          : `Manual · Within 25km zone · no delivery fee`}
                      </div>
                    );
                  })()}
                </div>
                <div>
                  {!fleet && (<>
                  <Label className="label-tag flex items-center gap-1.5" style={{ color: "#FBBF24" }}>
                    <Sparkles className="w-3 h-3" /> Extras
                  </Label>
                  <ExtraTagPicker tags={extraTags} selectedIds={form.extra_tag_ids} onChange={(ids) => setForm({ ...form, extra_tag_ids: ids })} testid="new-job-extra-tags" />
                  <div className="label-tag mt-2">One-off surcharges</div>
                  <SurchargePicker
                    items={surcharges}
                    selectedIds={form.surcharge_ids}
                    onChange={(ids) => {
                      if (oohSurcharge && jobIsOoh && !ids.includes(oohSurcharge.id)) setOohDismissed(true);
                      setForm({ ...form, surcharge_ids: ids });
                    }}
                    testid="new-job-surcharges"
                  />
                  {jobIsOoh && oohSurcharge && (
                    (form.surcharge_ids || []).includes(oohSurcharge.id) ? (
                      <div className="mono text-[10px] uppercase tracking-widest mt-1.5 px-2 py-1 border border-primary-orange text-primary-orange" data-testid="ooh-fee-note">
                        Out of hours fee applied — ${Number(oohSurcharge.amount).toLocaleString()}
                      </div>
                    ) : (
                      <div className="mono text-[10px] uppercase tracking-widest mt-1.5 px-2 py-1 border border-red-500 text-red-500" data-testid="ooh-fee-waived">
                        Out of hours pour · fee removed — no ${Number(oohSurcharge.amount).toLocaleString()} will be charged
                      </div>
                    )
                  )}
                  <Input data-testid="job-extras" value={form.extras} onChange={(e) => setForm({ ...form, extras: e.target.value })} placeholder="Additional notes (optional)" className="rounded-none bg-transparent border-industrial mono mt-2 h-8 text-xs" />
                  </>)}
                  <div className="label-tag mt-3 flex items-center justify-between">
                    <span>Notes / site instructions</span>
                    <label className="flex items-center gap-1.5 cursor-pointer normal-case">
                      <input
                        type="checkbox"
                        data-testid="job-notes-to-driver"
                        checked={!!form.notes_to_driver}
                        onChange={(e) => setForm({ ...form, notes_to_driver: e.target.checked })}
                        className="w-3.5 h-3.5 accent-[rgb(var(--primary))]"
                      />
                      <span className="mono text-[10px] uppercase tracking-widest text-primary-orange">Send to driver</span>
                    </label>
                  </div>
                  <textarea
                    data-testid="job-notes"
                    value={form.notes}
                    onChange={(e) => setForm({ ...form, notes: e.target.value })}
                    rows={3}
                    placeholder="e.g. Gate code 4821 · park on the verge · ask for Bevan · low clearance at entry"
                    className="w-full rounded-none bg-transparent border border-industrial mono text-xs p-2"
                  />
                  {form.notes && form.notes_to_driver && (
                    <div className="mono text-[10px] uppercase tracking-widest text-muted-industrial mt-1">Shows on the driver's job screen</div>
                  )}
                </div>

                {/* All-up price BEFORE it's delivered, so the customer can be told on the phone. */}
                {!fleet && (
                <OrderQuote
                  form={{ ...form, lines: form.mix_id ? [{ mix_id: form.mix_id, quantity_m3: form.quantity_m3, price_per_m3: form.price_per_m3 ?? null }] : [] }}
                  customerName={customers.find((c) => c.id === form.customer_id)?.name || form.walk_in_name}
                  sitePhone={form.site_contact_phone}
                  plantId={plantId}
                  onRate={(mixId, rate) => setForm((f) => ({ ...f, price_per_m3: rate }))}
                />
                )}
              </div>
              <DialogFooter className="px-6 py-3 shrink-0 border-t border-industrial bg-surface">
                <Button data-testid="job-save-btn" onClick={() => createJob()} className="rounded-none bg-primary-orange hover:bg-[rgb(var(--primary-hover))] uppercase tracking-widest text-xs font-bold">Create Job</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* ─── Main layout: fleet column + job table ─────────────────── */}
      <div className="flex-1 flex gap-2 min-h-0">
        {/* ─── Vertical Fleet Kanban — collapses to a slim rail (w-9) ────── */}
        <div
          className={`shrink-0 card-industrial flex flex-col overflow-hidden transition-[width] duration-200 ${fleetCollapsed ? "w-9 px-0.5 py-1" : "w-[190px] px-1.5 py-2"}`}
          data-testid="fleet-kanban"
          data-collapsed={fleetCollapsed ? "1" : "0"}
        >
          <div className={`flex items-center justify-between border-b border-industrial mb-1 ${fleetCollapsed ? "px-0 pb-1" : "px-1 pb-1.5"}`}>
            {!fleetCollapsed && (
              <div className="min-w-0">
                <div className="label-tag">Fleet</div>
                <div className="mono text-[9px] text-muted-industrial tracking-widest uppercase mt-0.5">
                  {availableTrucks.length}/{trucks.length} avail · drag to change
                </div>
              </div>
            )}
            <button
              type="button"
              data-testid="fleet-collapse-toggle"
              onClick={() => setFleetCollapsed((v) => !v)}
              title={fleetCollapsed ? "Expand fleet lanes" : "Collapse fleet to slim rail"}
              className="ml-auto inline-flex items-center justify-center w-6 h-6 border border-industrial text-muted-industrial hover:text-primary-orange hover:border-primary-orange"
            >
              {fleetCollapsed ? <ChevronsRight className="w-3 h-3" /> : <ChevronsLeft className="w-3 h-3" />}
            </button>
          </div>
          <div className={`flex-1 overflow-y-auto pr-0.5 ${fleetCollapsed ? "space-y-1" : "space-y-1.5"}`}>
            {LANE_ORDER.map((laneKey) => {
              const meta = LANE_META[laneKey];
              const laneTrucks = trucks.filter((t) => (LANE_OF[t.status] || "available") === laneKey);
              const isDragOver = dragOverLane === laneKey;
              if (fleetCollapsed) {
                // Slim rail: colour tab with count. Click to expand + focus lane.
                return (
                  <button
                    key={laneKey}
                    type="button"
                    data-testid={`fleet-lane-mini-${laneKey}`}
                    onDragOver={(e) => handleLaneDragOver(e, laneKey)}
                    onDragLeave={handleLaneDragLeave}
                    onDrop={(e) => handleLaneDrop(e, laneKey)}
                    onClick={() => setFleetCollapsed(false)}
                    title={`${meta.label} · ${laneTrucks.length}`}
                    className={`w-full flex flex-col items-center gap-0.5 py-1 border transition-colors ${isDragOver ? "bg-primary-orange/10" : "hover:bg-surface-2/40"}`}
                    style={{ borderColor: meta.color }}
                  >
                    <span className="mono text-[10px] font-black" style={{ color: meta.color }}>
                      {laneTrucks.length}
                    </span>
                    <span className="mono text-[7px] uppercase tracking-widest truncate w-full text-center" style={{ color: meta.color }}>
                      {meta.label.slice(0, 4)}
                    </span>
                  </button>
                );
              }
              return (
                <div
                  key={laneKey}
                  data-testid={`fleet-lane-${laneKey}`}
                  onDragOver={(e) => handleLaneDragOver(e, laneKey)}
                  onDragLeave={handleLaneDragLeave}
                  onDrop={(e) => handleLaneDrop(e, laneKey)}
                  className={`border transition-colors ${isDragOver ? "bg-primary-orange/5" : "bg-surface-2/20"}`}
                  style={{ borderColor: isDragOver ? meta.color : "rgb(var(--border))" }}
                >
                  <div
                    className="flex items-center justify-between px-1.5 py-0.5 border-b"
                    style={{ borderColor: meta.color, background: `${meta.color}12` }}
                  >
                    <span className="mono text-[9px] font-bold tracking-widest truncate" style={{ color: meta.color }}>
                      {meta.label}
                    </span>
                    <span className="mono text-[11px] font-bold ml-1 shrink-0" style={{ color: meta.color }} data-testid={`fleet-lane-count-${laneKey}`}>
                      {laneTrucks.length}
                    </span>
                  </div>
                  <div className="p-1 flex flex-wrap gap-1 min-h-[24px]">
                    {laneTrucks.length === 0 && (
                      <div className="text-[8px] text-muted-industrial mono opacity-40 self-center w-full text-center">
                        —
                      </div>
                    )}
                    {laneTrucks.map((t) => {
                      const ps = preStartByTruck[t.id];
                      const needsPreStart = ps && (!ps.submitted || (ps.overall === "fail" && !ps.resolved));
                      const overridden = !!override[t.id];
                      // v3.7 · Once overridden the modal is skipped but the
                      // red flash MUST persist per the modal copy so the truck
                      // stays visually urgent until a pre-start is submitted.
                      const redFlash = needsPreStart;
                      const psTitle = redFlash
                        ? `${t.number} · PRE-START NOT DONE — drag will trigger safety warning`
                        : `${t.number} · ${t.driver_name || "no driver"} · ${t.capacity_m3}m³ — drag onto a job or another lane`;
                      const chip = (
                      <div
                        draggable
                        onDragStart={() => handleDragStart(t)}
                        data-testid={`truck-${t.number}`}
                        title={psTitle}
                        className={`border bg-surface inline-flex items-center gap-1 px-1.5 py-0.5 cursor-grab active:cursor-grabbing hover:bg-surface-2 ${redFlash ? "pulse-red-strong" : ""}`}
                        style={{ borderColor: redFlash ? "#EF4444" : meta.color }}
                      >
                        <Truck className="w-2.5 h-2.5 shrink-0" style={{ color: redFlash ? "#EF4444" : meta.color }} />
                        <span className={`mono text-[10px] font-bold ${redFlash ? "text-red-400 flash-red" : ""}`} data-testid={`truck-name-${t.number}`}>{t.number}</span>
                        {redFlash && (
                          <span className="w-1.5 h-1.5 bg-red-500 rounded-full animate-pulse ml-0.5" data-testid={`prestart-warn-${t.number}`} title="Pre-start not done" />
                        )}
                        {overridden && (
                          <span className="w-1.5 h-1.5 bg-amber-500 rounded-full ml-0.5" data-testid={`prestart-override-${t.number}`} title="Pre-start override for this session" />
                        )}
                      </div>
                      );
                      // Returning concrete trucks get the Wash Out / Wash Fins / Dump instruction row.
                      if (laneKey === "returning" && !fleet) {
                        return (
                          <div key={t.id} className="w-full border border-industrial bg-surface-2/40 p-1" data-testid={`dispatch-return-truck-${t.number}`}>
                            {chip}
                            <div className="flex items-center gap-1 mt-1 flex-wrap" data-testid={`dispatch-wash-tasks-${t.number}`}>
                              {[["wash_out", "Wash Out"], ["wash_fins", "Wash Fins"], ["dump_leftovers", "Dump"]].map(([k, label]) => {
                                const task = t.wash_tasks?.[k];
                                const on = !!task;
                                const acked = !!task?.acked_at;
                                return (
                                  <button key={k} type="button" onClick={() => toggleWash(t, k)}
                                    data-testid={`dispatch-wash-${k}-${t.number}`}
                                    title={on ? (acked ? `${label} — driver acknowledged` : `${label} sent — waiting for driver`) : `Send "${label}" to ${t.driver_name || "driver"}`}
                                    className={`mono text-[9px] uppercase tracking-wider px-1.5 py-0.5 border transition-colors ${
                                      on
                                        ? acked
                                          ? "border-emerald-500 text-emerald-400"
                                          : "border-primary-orange text-primary-orange bg-primary-orange/10 animate-pulse"
                                        : "border-industrial text-muted-industrial hover:border-primary-orange hover:text-primary-orange"
                                    }`}>
                                    {acked ? "✓ " : ""}{label}
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        );
                      }
                      return <div key={t.id}>{chip}</div>;
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Job table (vertical rows) — takes primary real estate */}
        <div className="flex-1 card-industrial overflow-hidden flex flex-col min-w-0">
          {/* Column headers — sticky. v3.10d · Trucks header is now a
              3-sub-column Progress Header (To-Job / On-Job / Returning) so
              each job row can mirror the same lane grid underneath. */}
          <div className="grid items-center gap-2 px-3 py-2 border-b border-industrial bg-surface-2 text-[9px] mono uppercase tracking-widest text-muted-industrial shrink-0"
               style={{ gridTemplateColumns: "90px minmax(220px, 1.4fr) 78px minmax(140px, 1fr) 90px 200px 54px minmax(300px, 1.8fr) minmax(110px, max-content)" }}>
            <div>Date / Time</div>
            <div>Customer / Address</div>
            <div>Mix</div>
            <div className="flex items-center gap-1" style={{ color: "#FBBF24" }}><Sparkles className="w-2.5 h-2.5" />Extras</div>
            <div className="text-right">Order</div>
            <div>Progressive</div>
            <div className="text-center" title="Order flags · OVER = committed volume exceeds the order">Flags</div>
            <div className="min-w-0">
              <div className="flex items-center justify-between gap-2 mb-1">
                <span>Trucks</span>
                {/* v3.10c · Compact-View toggle — persisted in localStorage */}
                <button
                  type="button"
                  data-testid="compact-view-toggle"
                  onClick={() => setCompactView((v) => !v)}
                  title={compactView ? "Compact view · click for detailed chips" : "Detailed view · click for compact"}
                  className={`mono text-[9px] uppercase tracking-widest px-1.5 py-0.5 border transition-colors ${
                    compactView
                      ? "border-primary-orange text-primary-orange bg-primary-orange/10"
                      : "border-industrial text-muted-industrial hover:border-primary-orange hover:text-primary-orange"
                  }`}
                >
                  {compactView ? "Compact ●" : "Compact ○"}
                </button>
              </div>
              {compactView && (
                <div className="grid grid-cols-3 gap-1 text-[8px] tracking-[0.18em]" data-testid="trucks-progress-header">
                  <div className="flex items-center gap-1 px-1 border-l-2" style={{ borderColor: "#F97316", color: "#F97316" }}>
                    → To-Job
                  </div>
                  <div className="flex items-center gap-1 px-1 border-l-2" style={{ borderColor: "#A855F7", color: "#A855F7" }}>
                    ▸ On-Job
                  </div>
                  <div className="flex items-center gap-1 px-1 border-l-2" style={{ borderColor: "#0EA5E9", color: "#0EA5E9" }}>
                    ← Returning
                  </div>
                </div>
              )}
            </div>
            <div className="text-right">Actions</div>
          </div>

          <div className="flex-1 overflow-y-auto" data-testid="jobs-list">
            {orderedJobs.length === 0 && cancelledJobs.length === 0 && (
              <div className="p-10 text-center text-muted-industrial">
                No jobs for <span className="mono text-primary-orange">{viewDate}</span>. {viewDate === today ? "Click New Job to schedule one." : "Use the date arrows to browse another day."}
              </div>
            )}

            {orderedJobs.map((j) => {
              const assignedTrucks = trucks.filter((t) => j.assigned_truck_ids?.includes(t.id));
              const isCompleted = j.status === "completed";
              const hasExtras = !!(j.extras && j.extras.trim());
              const mc = mixColor(j.mix_code);
              // v3.16 — REAL-TIME COMMITMENT tracking (three-state progressive bar).
              //   • DELIVERED = job.delivered_m3 (dockets fully delivered; solid orange)
              //   • IN-TRANSIT = trucks currently loading / en-route / on-site / discharging
              //     (light orange). Uses truck_loads[truck.id] when the operator has
              //     already assigned a specific volume, otherwise falls back to the
              //     truck's rated capacity so the bar reacts the instant a truck is
              //     dropped onto the job.
              //   • REMAINING = ordered − (delivered + inTransit); dark grey.
              const IN_TRANSIT_STATES = new Set(["loading", "slumping", "en_route", "on_site", "discharging"]);
              const delivered = Number(j.delivered_m3 || 0);
              const ordered = Number(j.quantity_m3 || 0) || 1;
              // On-truck volume: its booked load, else what's still owed on the job
              // (capped by the barrel) — not the barrel size, which over-states it.
              let _left = Math.max(0, ordered - delivered);
              const inTransit = assignedTrucks
                .filter((t) => IN_TRANSIT_STATES.has(t.status))
                .reduce((s, t) => {
                  const explicit = j.truck_loads?.[t.id];
                  const cap = Number(t.capacity_m3 || 0);
                  let v = explicit != null
                    ? (_left > 0 ? Math.min(Number(explicit), _left) : Number(explicit))
                    : Math.min(_left || cap, cap || _left);
                  if (!Number.isFinite(v) || v < 0) v = 0;
                  _left = Math.max(0, _left - v);
                  return s + v;
                }, 0);
              const committed = delivered + inTransit;
              const overOrder = committed > ordered + 0.001;
              const deliveredPct = Math.min(100, (delivered / ordered) * 100);
              const inTransitPct = Math.max(0, Math.min(100 - deliveredPct, (inTransit / ordered) * 100));
              const committedPct = Math.min(100, (committed / ordered) * 100);
              // Legacy `loaded` still drives the compact/per-line percentage below.
              const loaded = Math.max(delivered, inTransit + delivered);
              const pct = committedPct;
              // Compute the next scheduled load time and whether the row should pulse
              const nextLoad = !isCompleted ? computeNextLoad(j, dockets, nowMs) : null;
              const anyTruckLoading = assignedTrucks.some((t) => t.status === "loading");
              const shouldPulse = !!(nextLoad && nextLoad.pulse && !anyTruckLoading);

              return (
                <div
                  key={j.id}
                  data-testid={`job-row-${j.id}`}
                  data-status={j.status}
                  onDragOver={(e) => !isCompleted && handleDragOver(e, j.id)}
                  onDragLeave={handleDragLeave}
                  onDrop={(e) => !isCompleted && handleDrop(e, j)}
                  className={`border border-industrial mx-2 mb-1.5 first:mt-2 hover:bg-surface-2/40 transition-colors ${dragOverJob === j.id ? "bg-primary-orange/10 border-primary-orange" : ""} ${isCompleted ? "opacity-55" : ""} ${flashJobIds.has(j.id) ? "job-row-flash" : ""} ${shouldPulse ? "job-row-load-due" : ""}`}
                  style={{ borderLeft: `3px solid ${mc.hex}` }}
                >
                  <div className={`grid items-center gap-2 px-3 ${compactView ? "py-1" : "py-1.5"}`}
                       style={{ gridTemplateColumns: "90px minmax(220px, 1.4fr) 78px minmax(140px, 1fr) 90px 200px 54px minmax(300px, 1.8fr) minmax(110px, max-content)" }}>
                    {/* Date/Time */}
                    <div>
                      <div className="mono text-sm font-bold text-primary-orange leading-none">{j.time_required || "—"}</div>
                      <div className="mono text-[9px] uppercase text-muted-industrial mt-0.5">{j.date}</div>
                      {nextLoad && !nextLoad.allDone && (
                        <div
                          data-testid={`next-load-${j.id}`}
                          title={`Truck ${nextLoad.slotIndex} of ${nextLoad.totalSlots} · every ${j.load_spacing_min}min`}
                          className={`mono text-[9px] mt-1 inline-block px-1 py-0.5 border ${shouldPulse ? "border-primary-orange text-primary-orange font-bold" : "border-industrial text-muted-industrial"}`}
                        >
                          NEXT {nextLoad.nextTime}
                          {nextLoad.minutesUntil <= 0
                            ? <span className="ml-1 text-red-400">OVERDUE</span>
                            : nextLoad.minutesUntil <= 15
                              ? <span className="ml-1">· {nextLoad.minutesUntil}m</span>
                              : null}
                        </div>
                      )}
                      {nextLoad?.allDone && (
                        <div className="mono text-[9px] mt-1 inline-block px-1 py-0.5 border border-emerald-600/60 text-emerald-500">
                          ALL LOADED
                        </div>
                      )}
                    </div>
                    {/* Customer / Address — click to edit */}
                    <div className="min-w-0">
                      <button
                        type="button"
                        data-testid={`customer-cell-${j.id}`}
                        onClick={(e) => { e.stopPropagation(); openEdit(j); }}
                        className="w-full min-w-0 text-left cursor-pointer group -mx-1 px-1 py-0.5 rounded-sm hover:bg-primary-orange/5"
                        title="Click to edit order"
                      >
                        <div className="heading text-sm font-bold truncate flex items-center gap-2 group-hover:text-primary-orange transition-colors">
                          {j.customer_name}
                          {isCompleted && <span className="mono text-[8px] uppercase tracking-widest px-1 py-0.5 border border-primary-orange text-primary-orange shrink-0">CLOSED</span>}
                          <Pencil className="w-3 h-3 opacity-0 group-hover:opacity-70 text-primary-orange shrink-0" />
                        </div>
                      </button>
                      <div className="text-[11px] text-muted-industrial truncate flex items-center gap-1 -mx-1 px-1" title={j.site_address || j.site_name}>
                        <MapPin className="w-2.5 h-2.5 shrink-0" />
                        <span className="truncate">{j.site_address || j.site_name || "—"}</span>
                        {!isCompleted && (
                          <AddressQuickEdit job={j} onSaved={load} />
                        )}
                        {j.po_number && <span className="mono text-[9px] text-muted-industrial ml-1 shrink-0">· PO {j.po_number}</span>}
                      </div>
                      {j.pump_unit && (
                        <div className="text-[10px] mono text-sky-400 flex items-center gap-1 pl-3.5 truncate" data-testid={`job-pump-unit-${j.id}`}>
                          <span className="truncate">PUMP · {j.pump_unit.company} {j.pump_unit.name}</span>
                          {j.pump_unit.operator_name && <span className="text-muted-industrial truncate">· {j.pump_unit.operator_name}</span>}
                          {j.pump_unit.phone && <a href={`tel:${j.pump_unit.phone}`} className="underline hover:text-sky-300 shrink-0">{j.pump_unit.phone}</a>}
                        </div>
                      )}
                      {j.delivery_km != null && (
                        <div className="text-[10px] mono text-muted-industrial flex items-center gap-1 pl-3.5" data-testid={`job-km-${j.id}`}>
                          <span>{Number(j.delivery_km).toFixed(1)} km</span>
                          {j.delivery_fee > 0 && <span className="text-primary-orange">· ${Number(j.delivery_fee).toFixed(2)} delivery fee</span>}
                          {j.delivery_fee === 0 && <span className="text-emerald-500">· in free zone</span>}
                        </div>
                      )}
                    </div>
                    {/* Mix — shows all lines when the order has multiple mixes */}
                    <div className="space-y-0.5">
                      {(j.lines && j.lines.length > 1) ? (
                        <>
                          {j.lines.map((ln, li) => {
                            const lmc = mixColor(ln.mix_code);
                            return (
                              <div key={ln.id || li} className="flex items-center gap-1.5" data-testid={`job-line-${j.id}-${li}`}>
                                <span
                                  className="mono text-[10px] uppercase tracking-widest px-1.5 py-0.5 border inline-block"
                                  style={{ borderColor: lmc.border, color: lmc.hex, background: lmc.bg }}
                                >
                                  {ln.mix_code}
                                </span>
                                <span className="mono text-[10px] text-muted-industrial">{ln.quantity_m3}m³</span>
                              </div>
                            );
                          })}
                          {!isCompleted && <SlumpQuickEdit job={j} onSaved={load} />}
                        </>
                      ) : (
                        <>
                          <span
                            className="mono text-[10px] uppercase tracking-widest px-1.5 py-0.5 border inline-block"
                            style={{ borderColor: mc.border, color: mc.hex, background: mc.bg }}
                            data-testid={`mix-pill-${j.mix_code}`}
                          >
                            {j.mix_code}
                          </span>
                          {!isCompleted ? (
                            <SlumpQuickEdit job={j} onSaved={load} />
                          ) : (
                            j.slump_mm && <div className="mono text-[9px] text-muted-industrial mt-0.5">{j.slump_mm}mm</div>
                          )}
                        </>
                      )}
                    </div>
                    {/* Extras */}
                    <div className="min-w-0 space-y-1">
                      {(j.extra_tag_ids || []).length > 0 && (
                        <div className="flex flex-wrap gap-1" data-testid={`extra-tags-${j.id}`}>
                          {(j.extra_tag_ids || []).map((tid) => {
                            const t = extraTags.find((x) => x.id === tid);
                            if (!t) return null;
                            return (
                              <span
                                key={tid}
                                className="mono text-[9px] uppercase tracking-widest px-1.5 py-0.5 font-bold"
                                style={{ background: t.color, color: "#ffffff" }}
                                title={t.price_per_m3 > 0 ? `+$${t.price_per_m3.toFixed(2)}/m³` : t.name}
                              >
                                {t.name}
                              </span>
                            );
                          })}
                        </div>
                      )}
                      {hasExtras && (
                        <div className="border-l-2 border-[#FBBF24] bg-[#FBBF24]/5 px-2 py-1" data-testid={`extras-${j.id}`}>
                          <div className="text-[11px] text-ink leading-tight break-words">{j.extras}</div>
                        </div>
                      )}
                      {!hasExtras && (j.extra_tag_ids || []).length === 0 && (
                        <span className="text-muted-industrial text-[11px]">—</span>
                      )}
                    </div>
                    {/* Order — click the qty to top-up (+ extra m³ mid-pour) */}
                    <div className="text-right">
                      <JobTopupChip job={j} onSaved={load} disabled={isCompleted} />
                    </div>
                    {/* Progressive — per-mix bars when multi-line, otherwise aggregate */}
                    <div>
                      {(j.lines && j.lines.length > 1) ? (
                        <div className="space-y-1" data-testid={`per-mix-progress-${j.id}`}>
                          {j.lines.map((ln, li) => {
                            const linePct = Math.min(100, ((ln.delivered_m3 || 0) / Math.max(1, ln.quantity_m3)) * 100);
                            const lmc = mixColor(ln.mix_code);
                            return (
                              <div key={ln.id || li}>
                                <div className="flex items-center gap-1.5">
                                  <span className="mono text-[9px] font-bold" style={{ color: lmc.hex }}>{ln.mix_code}</span>
                                  <div className="h-1 bg-surface-2 flex-1 border border-industrial">
                                    <div className="h-full transition-all" style={{ width: `${linePct}%`, background: lmc.hex }} />
                                  </div>
                                  <span className="mono text-[9px] text-muted-industrial whitespace-nowrap">
                                    {ln.delivered_m3 || 0}/{ln.quantity_m3}
                                  </span>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <div className="flex flex-col gap-1 min-w-0" data-testid={`progressive-cell-${j.id}`}>
                          {/* TOP ROW · bar only (grey remaining · solid orange delivered · light orange in transit) */}
                          <div
                            data-testid={`progressive-bar-${j.id}`}
                            data-over-order={overOrder ? "1" : "0"}
                            title={`Delivered ${delivered.toFixed(1)}m³ · In transit ${inTransit.toFixed(1)}m³ · Remaining ${Math.max(0, ordered - committed).toFixed(1)}m³`}
                            className={`h-2.5 bg-surface-2 border flex overflow-hidden ${overOrder ? "border-red-500 progressive-over-order" : "border-industrial"}`}
                          >
                            <div className={`h-full transition-all ${overOrder ? "bg-red-500" : "bg-primary-orange"}`} style={{ width: `${deliveredPct}%` }} />
                            <div className={`h-full transition-all ${overOrder ? "bg-red-500/50" : "bg-primary-orange/35"}`} style={{ width: `${inTransitPct}%` }} />
                          </div>
                          {/* BOTTOM ROW · text only, never wraps into the next column */}
                          <div
                            className="mono text-[11px] whitespace-nowrap truncate leading-tight"
                            title={`${pct.toFixed(0)}% · ${delivered.toFixed(1)} of ${ordered}m³ delivered${inTransit > 0 ? ` · ${inTransit.toFixed(1)}m³ in transit` : ""}`}
                            data-testid={`progressive-text-${j.id}`}
                          >
                            <span className={overOrder ? "text-red-500 font-bold" : "text-primary-orange font-bold"}>{pct.toFixed(0)}%</span>
                            <span className="text-muted-industrial"> · </span>
                            <span className="text-ink">{delivered.toFixed(1)}m³</span>
                            {inTransit > 0 && (
                              <>
                                <span className="text-muted-industrial"> · </span>
                                <span className={overOrder ? "text-red-500" : "text-primary-orange/80"}>In Transit</span>
                              </>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                    {/* Flags — OVER badge + recount live in their own column so they never collide with text or trucks */}
                    <div className="flex flex-col items-center gap-1" data-testid={`flags-cell-${j.id}`}>
                      {overOrder && (
                        <span
                          data-testid={`over-order-warning-${j.id}`}
                          className="mono text-[9px] uppercase tracking-widest px-1 py-0.5 border border-red-500 text-red-500 bg-red-500/10 font-bold"
                        >
                          OVER
                        </span>
                      )}
                      {!isCompleted && (
                        <button
                          type="button"
                          data-testid={`progressive-refresh-${j.id}`}
                          onClick={(e) => { e.stopPropagation(); recountJob(j.id); }}
                          title="Force recount from live dockets"
                          className="text-muted-industrial hover:text-primary-orange"
                        >
                          <RefreshCcw className="w-3 h-3" />
                        </button>
                      )}
                    </div>
                    {/* Trucks — v3.10d Progress Header lanes when Compact is on */}
                    {compactView ? (
                      <div
                        className="grid gap-1 items-start min-w-0 [grid-template-columns:repeat(auto-fit,minmax(150px,1fr))]"
                        data-testid={`trucks-lanes-${j.id}`}
                        onDragOver={(e) => !isCompleted && handleDragOver(e, j.id)}
                        onDrop={(e) => !isCompleted && handleDrop(e, j)}
                      >
                        {(() => {
                          // Lane assignment per truck status:
                          //   loading       → To-Job lane, dashed "queued"
                          //   en_route      → To-Job lane, solid active
                          //   on_site/disc  → On-Job lane
                          //   returning     → Returning lane
                          //   available/oos → hidden (delivery done)
                          const lanes = { toJob: [], onJob: [], returning: [] };
                          assignedTrucks.forEach((t) => {
                            if (t.status === "loading" || t.status === "slumping" || t.status === "en_route") lanes.toJob.push(t);
                            else if (t.status === "on_site" || t.status === "discharging") lanes.onJob.push(t);
                            else if (t.status === "returning") lanes.returning.push(t);
                          });
                          const emptyPlaceholder = (
                            <span className="mono text-[9px] text-muted-industrial/50 pl-1">—</span>
                          );
                          const renderLane = (list, laneKey, testid) => (
                            <div
                              className="flex flex-wrap gap-1 items-start min-h-[24px] pl-1 border-l"
                              style={{ borderColor:
                                laneKey === "toJob" ? "#F97316" :
                                laneKey === "onJob" ? "#A855F7" : "#0EA5E9" }}
                              data-testid={testid}
                            >
                              {list.length === 0 ? emptyPlaceholder : list.map((t) => {
                                const b = currentBatchFor(j.id, t);
                                const dk = b?.docket_id
                                  ? dockets.find((d) => d.id === b.docket_id)
                                  : dockets.find((d) => d.job_id === j.id && d.truck_id === t.id && d.status !== "delivered");
                                const ps = preStartByTruck[t.id];
                                const needsPS = ps && (!ps.submitted || (ps.overall === "fail" && !ps.resolved));
                                return (
                                  <TruckMicroBadge
                                    key={t.id}
                                    truck={t}
                                    job={j}
                                    docket={dk}
                                    needsPreStart={needsPS}
                                    prediction={predictions[t.id]}
                                  />
                                );
                              })}
                            </div>
                          );
                          if (assignedTrucks.length === 0 && !isCompleted) {
                            // Drop zone spans all three lanes when nothing assigned
                            return (
                              <div className="col-span-full text-[10px] text-muted-industrial border border-dashed border-industrial px-2 py-1 text-center">
                                Drop truck here
                              </div>
                            );
                          }
                          if (assignedTrucks.length === 0 && isCompleted) {
                            return (
                              <div className="col-span-full text-[10px] text-muted-industrial">
                                Closed {j.completed_at ? `· ${new Date(j.completed_at).toLocaleTimeString()}` : ""}
                              </div>
                            );
                          }
                          return (
                            <>
                              {renderLane(lanes.toJob,    "toJob",    `trucks-lane-tojob-${j.id}`)}
                              {renderLane(lanes.onJob,    "onJob",    `trucks-lane-onjob-${j.id}`)}
                              {renderLane(lanes.returning,"returning",`trucks-lane-returning-${j.id}`)}
                            </>
                          );
                        })()}
                      </div>
                    ) : (
                      <div className="flex flex-wrap gap-1 items-center min-w-0">
                        {assignedTrucks.length === 0 && !isCompleted && (
                          <div className="text-[10px] text-muted-industrial border border-dashed border-industrial px-2 py-1 w-full text-center">
                            Drop truck here
                          </div>
                        )}
                        {assignedTrucks.length === 0 && isCompleted && (
                          <div className="text-[10px] text-muted-industrial">
                            Closed {j.completed_at ? `· ${new Date(j.completed_at).toLocaleTimeString()}` : ""}
                          </div>
                        )}
                        {assignedTrucks.map((t) => {
                          const b = currentBatchFor(j.id, t);
                          const dk = b?.docket_id
                            ? dockets.find((d) => d.id === b.docket_id)
                            : dockets.find((d) => d.job_id === j.id && d.truck_id === t.id && d.status !== "delivered");
                          return (
                            <TruckChip
                              key={t.id}
                              truck={t}
                              job={j}
                              prediction={predictions[t.id]}
                              batch={b}
                              docketByTruckJob={dk}
                              isCompleted={isCompleted}
                              onUnassign={(tr) => unassign(j, tr.id)}
                              onSwap={(tr) => openSwap(j, tr)}
                              onAdvance={advance}
                              onCancelBatch={cancelBatch}
                              onLoadChange={load}
                            />
                          );
                        })}
                      </div>
                    )}
                    {/* Actions */}
                    <div className="flex items-center gap-1 justify-end">
                      {!isCompleted && (
                        <button data-testid={`ai-optimise-${j.id}`} onClick={() => setOptimiseJobId(j.id)} title="AI truck suggestion"
                          className="mono text-[10px] uppercase text-primary-orange hover:text-black hover:bg-primary-orange inline-flex items-center gap-1 px-1.5 py-1 border border-primary-orange">
                          <Sparkles className="w-3 h-3" />
                        </button>
                      )}
                      <button data-testid={`share-job-${j.id}`} onClick={() => setShareJob(j)}
                        title="Generate Driver QR — no-login tracking link for the pump operator / site foreman"
                        className="mono text-[10px] uppercase inline-flex items-center gap-1 px-1.5 py-1 border text-muted-industrial border-industrial hover:border-primary-orange hover:text-primary-orange transition-colors">
                        <QrCode className="w-3 h-3" />
                      </button>
                      <button data-testid={`clone-job-${j.id}`} onClick={() => openClone(j)}
                        title={isCompleted ? "Reorder — duplicate this pour to a chosen day" : "Duplicate this job to a chosen day"}
                        className={`mono text-[10px] uppercase inline-flex items-center gap-1 px-1.5 py-1 border transition-colors ${
                          isCompleted
                            ? "border-primary-orange text-primary-orange bg-primary-orange/10 hover:bg-primary-orange hover:text-black font-black"
                            : "text-muted-industrial border-industrial hover:border-primary-orange hover:text-primary-orange"
                        }`}
                      >
                        <Copy className="w-3 h-3" />
                        {isCompleted && <span>Reorder</span>}
                      </button>
                      {!isCompleted && wxRisk && (
                        <button data-testid={`weather-text-${j.id}`} onClick={() => textConditions(j)}
                          title={j.weather_alert_sent_at ? `Conditions text sent ${new Date(j.weather_alert_sent_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}${j.weather_reply ? ` · customer replied: ${j.weather_reply.label}` : " · no reply yet"}` : "Text the customer about today's shrinkage-crack conditions — they reply 1 go ahead / 2 delay / 3 call me"}
                          className={`mono text-[10px] uppercase inline-flex items-center gap-1 px-1.5 py-1 border ${
                            j.weather_reply?.code === "delay" ? "border-red-500 text-red-400 bg-red-500/10 animate-pulse"
                            : j.weather_reply?.code === "go_ahead" ? "border-emerald-500 text-emerald-400"
                            : j.weather_reply?.code === "call_me" ? "border-amber-500 text-amber-400"
                            : j.weather_alert_sent_at ? "border-sky-500/60 text-sky-300"
                            : "border-amber-500/70 text-amber-400 hover:border-primary-orange hover:text-primary-orange"}`}>
                          <MessageSquareWarning className="w-3 h-3" />
                          {j.weather_reply?.code === "delay" && <span>Delay</span>}
                        </button>
                      )}
                      {!isCompleted && (
                        <button data-testid={`sms-track-${j.id}`} onClick={() => smsTrackingLink(j)} title="SMS live tracking link to customer"
                          className="mono text-[10px] uppercase text-muted-industrial hover:text-primary-orange inline-flex items-center gap-1 px-1.5 py-1 border border-industrial hover:border-primary-orange">
                          <MessageSquare className="w-3 h-3" />
                        </button>
                      )}
                      {!isCompleted ? (
                        <>
                          <button data-testid={`edit-job-${j.id}`} onClick={() => openEdit(j)} title="Edit"
                            className="mono text-[10px] uppercase text-muted-industrial hover:text-primary-orange inline-flex items-center gap-1 px-1.5 py-1 border border-industrial hover:border-primary-orange">
                            <Pencil className="w-3 h-3" />
                          </button>
                          <button data-testid={`close-job-${j.id}`} onClick={() => closeJob(j)} title="Close"
                            className="mono text-[10px] uppercase text-ink hover:text-primary-orange inline-flex items-center gap-1 px-1.5 py-1 border border-industrial hover:border-primary-orange">
                            <CheckCircle2 className="w-3 h-3" />
                          </button>
                        </>
                      ) : (
                        <button data-testid={`reopen-job-${j.id}`} onClick={() => reopenJob(j)} title="Reopen"
                          className="mono text-[10px] uppercase text-muted-industrial hover:text-primary-orange inline-flex items-center gap-1 px-1.5 py-1 border border-industrial hover:border-primary-orange">
                          <RotateCcw className="w-3 h-3" />
                        </button>
                      )}
                      {!isCompleted && (
                        <button data-testid={`cancel-job-${j.id}`} onClick={() => cancelJob(j)} title="Cancel this order — removes it from today's board"
                          className="mono text-[10px] uppercase font-bold inline-flex items-center gap-1 px-1.5 py-1 border border-red-500/70 text-red-400 hover:bg-red-500 hover:text-white hover:border-red-500 transition-colors">
                          <XOctagon className="w-3 h-3" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}

            {cancelledJobs.length > 0 && (
              <div className="border-t border-industrial mt-2 pt-2 px-3 pb-3" data-testid="cancelled-today">
                <div className="label-tag text-[10px] text-muted-industrial mb-2 flex items-center gap-1.5">
                  <XOctagon className="w-3 h-3 text-red-400" /> Cancelled today · {cancelledJobs.length}
                </div>
                <div className="space-y-1">
                  {cancelledJobs.map((j) => (
                    <div key={j.id} data-testid={`cancelled-row-${j.id}`}
                      className="flex items-center justify-between gap-3 border border-industrial/60 bg-red-500/5 px-2.5 py-1.5">
                      <div className="min-w-0 flex items-center gap-2 text-xs">
                        <span className="line-through text-muted-industrial truncate font-bold">{j.customer_name || "—"}</span>
                        {j.time_required && <span className="mono text-[10px] text-muted-industrial">{j.time_required}</span>}
                        {j.mix_code && <span className="mono text-[10px] text-muted-industrial truncate">{j.mix_code}</span>}
                        {(j.quantity_m3 != null) && <span className="mono text-[10px] text-muted-industrial">{j.quantity_m3}m³</span>}
                      </div>
                      <button data-testid={`undo-cancel-${j.id}`} onClick={() => undoCancelJob(j)} title="Undo — restore this order to the board"
                        className="shrink-0 mono text-[10px] uppercase font-bold inline-flex items-center gap-1 px-2 py-1 border border-primary-orange text-primary-orange hover:bg-primary-orange hover:text-black transition-colors">
                        <RotateCcw className="w-3 h-3" /> Undo
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Edit dialog */}

      <AIOptimiserDialog
        open={!!optimiseJobId}
        onOpenChange={(v) => { if (!v) setOptimiseJobId(null); }}
        jobId={optimiseJobId}
        onAssign={() => load()}
      />
      <JobShareQR open={!!shareJob} onOpenChange={(v) => { if (!v) setShareJob(null); }} job={shareJob} />

      {/* Clone / Reorder — pick which day the duplicate lands on */}
      <Dialog open={!!cloneTarget} onOpenChange={(v) => { if (!v) setCloneTarget(null); }}>
        <DialogContent className="rounded-none bg-surface border-industrial max-w-sm" data-testid="clone-job-dialog">
          <DialogHeader>
            <DialogTitle className="heading uppercase text-primary-orange">Duplicate job</DialogTitle>
          </DialogHeader>
          {cloneTarget && (
            <div className="space-y-4">
              <div className="text-sm text-muted-industrial">
                Copy <span className="text-ink font-bold">{cloneTarget.customer_name}</span>
                {cloneTarget.mix_code ? <> · {cloneTarget.mix_code}</> : null}
                {cloneTarget.quantity_m3 ? <> · {cloneTarget.quantity_m3} m³</> : null} to:
              </div>
              <div>
                <Label className="mono text-[10px] uppercase tracking-widest text-muted-industrial">Delivery date</Label>
                <Input
                  type="date"
                  value={cloneDate}
                  onChange={(e) => setCloneDate(e.target.value)}
                  data-testid="clone-date-input"
                  className="rounded-none bg-surface-2 border-industrial mt-1"
                />
                <div className="flex gap-2 mt-2">
                  <button type="button" onClick={() => setCloneDate(localToday())} data-testid="clone-date-today"
                    className="mono text-[10px] uppercase px-2 py-1 border border-industrial text-muted-industrial hover:border-primary-orange hover:text-primary-orange transition-colors">Today</button>
                  <button type="button" onClick={() => setCloneDate(shiftLocalDate(localToday(), 1))} data-testid="clone-date-tomorrow"
                    className="mono text-[10px] uppercase px-2 py-1 border border-industrial text-muted-industrial hover:border-primary-orange hover:text-primary-orange transition-colors">Tomorrow</button>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setCloneTarget(null)} className="rounded-none border-industrial uppercase text-xs">Cancel</Button>
            <Button onClick={confirmClone} disabled={cloning || !cloneDate} data-testid="clone-confirm-btn"
              className="rounded-none bg-primary-orange hover:bg-[rgb(var(--primary-hover))] text-black uppercase text-xs font-black">
              {cloning ? "Cloning…" : "Duplicate"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <SmsComposeDialog
        open={!!smsCompose}
        onOpenChange={(v) => { if (!v) setSmsCompose(null); }}
        phone={smsCompose?.phone || ""}
        message={smsCompose?.message || ""}
        trackingUrl={smsCompose?.trackingUrl || ""}
        customerName={smsCompose?.customerName || ""}
      />
      <Dialog open={!!editJob} onOpenChange={(v) => { if (!v) setEditJob(null); }}>
        <DialogContent className="rounded-none bg-surface border-industrial max-w-lg max-h-[92vh] flex flex-col p-0" data-testid="edit-job-dialog">
          <DialogHeader className="px-6 pt-6 pb-3 border-b border-industrial shrink-0"><DialogTitle className="heading uppercase">Edit Order</DialogTitle></DialogHeader>
          <div className="flex-1 overflow-y-auto px-6 py-4">
          {editJob && (
            <div className="space-y-3">
              <div className="text-xs text-muted-industrial">
                <span className="mono text-primary-orange">{editJob.customer_name}</span>
                {editJob.mix_code && editJob.mix_id === editForm.mix_id && <> · Current mix <span className="mono">{editJob.mix_code}</span></>}
                {editJob.mix_id !== editForm.mix_id && editForm.mix_id && (
                  <> · <span className="text-primary-orange mono">switching {editJob.mix_code} → {mixes.find((m) => m.id === editForm.mix_id)?.code || "?"}</span></>
                )}
                {editJob.delivered_m3 > 0 && <> · <span className="text-primary-orange">{editJob.delivered_m3}m³ already delivered</span></>}
              </div>

              {/* v3.4h · Customer re-assignment (fix wrong customer on a dispatched job) */}
              <div>
                <Label className="label-tag">Customer</Label>
                <Select value={editForm.customer_id} onValueChange={(v) => setEditForm({ ...editForm, customer_id: v })}>
                  <SelectTrigger data-testid="edit-job-customer" className="rounded-none bg-transparent border-industrial">
                    <SelectValue placeholder="Pick customer" />
                  </SelectTrigger>
                  <SelectContent className="rounded-none bg-surface border-industrial max-h-64">
                    {customers.map((c) => (
                      <SelectItem key={c.id} value={c.id}>{c.name}{c.customer_type === "cash" ? " · Cash" : ""}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {editForm.customer_id && editForm.customer_id !== editJob.customer_id && (
                  <div className="mt-1.5 mono text-[10px] uppercase tracking-widest border border-primary-orange/50 bg-primary-orange/10 text-primary-orange px-2 py-1.5" data-testid="edit-job-customer-warning">
                    Re-assigning to <b>{customers.find((c) => c.id === editForm.customer_id)?.name}</b>.
                    Uninvoiced dockets on this job will be moved too. Site is being cleared — pick a new site below.
                  </div>
                )}
              </div>
              <div className="border border-industrial p-3 space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="label-tag flex items-center gap-2">
                    Mix Lines
                    {editJob.delivered_m3 > 0 && (
                      <span className="text-primary-orange normal-case font-normal text-[10px]">(delivered qty locks the line to that mix)</span>
                    )}
                  </Label>
                  <button
                    type="button"
                    data-testid="edit-add-mix-line"
                    onClick={() => setEditForm({ ...editForm, lines: [...(editForm.lines || []), { mix_id: "", quantity_m3: 3, delivered_m3: 0 }] })}
                    className="mono uppercase text-[10px] tracking-widest text-primary-orange hover:opacity-80"
                  >
                    + Add mix
                  </button>
                </div>
                {(editForm.lines || []).map((ln, i) => {
                  const delivered = Number(ln.delivered_m3 || 0);
                  const locked = delivered > 0;
                  return (
                    <div key={ln.id || i} className="grid grid-cols-[1fr_100px_28px] gap-2 items-center" data-testid={`edit-mix-line-${i}`}>
                      <Select
                        value={ln.mix_id}
                        onValueChange={(v) => {
                          const arr = [...editForm.lines]; arr[i] = { ...arr[i], mix_id: v, price_per_m3: null }; setEditForm({ ...editForm, lines: arr });
                        }}
                        disabled={locked}
                      >
                        <SelectTrigger className="rounded-none bg-transparent border-industrial mono">
                          <SelectValue placeholder="Select mix" />
                        </SelectTrigger>
                        <SelectContent className="rounded-none bg-surface border-industrial max-h-[240px]">
                          {sortMixes(mixes).map((m) => (
                            <SelectItem key={m.id} value={m.id}>
                              <span className="mono font-bold">{m.code}</span> — {m.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <div>
                        <Input
                          type="number"
                          step="0.2"
                          min={delivered || 0}
                          value={ln.quantity_m3}
                          onChange={(e) => {
                            const arr = [...editForm.lines]; arr[i] = { ...arr[i], quantity_m3: parseFloat(e.target.value) || 0 }; setEditForm({ ...editForm, lines: arr });
                          }}
                          className="rounded-none bg-transparent border-industrial mono"
                          placeholder="m³"
                        />
                        {locked && (
                          <div className="mono text-[9px] text-primary-orange mt-0.5 uppercase tracking-widest">{delivered}m³ delivered</div>
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={() => setEditForm({ ...editForm, lines: editForm.lines.filter((_, j) => j !== i) })}
                        disabled={locked || (editForm.lines || []).length <= 1}
                        className="h-8 w-7 flex items-center justify-center text-red-500 hover:text-red-400 disabled:text-neutral-700 disabled:cursor-not-allowed"
                        data-testid={`edit-remove-mix-line-${i}`}
                        title={locked ? "Cannot remove — has delivered dockets" : "Remove line"}
                      >
                        ×
                      </button>
                    </div>
                  );
                })}
                <div className="mono text-[10px] uppercase tracking-widest text-muted-industrial">
                  Order total: {((editForm.lines || []).reduce((s, l) => s + Number(l.quantity_m3 || 0), 0)).toFixed(1)} m³
                </div>
              </div>

              {/* ── Repricing audit — visible when the job has delivered dockets ── */}
              {editJob.delivered_m3 > 0 && (
                <div className="border border-industrial p-2 bg-surface-2/40">
                  <div className="flex items-center justify-between gap-2">
                    <div className="label-tag flex items-center gap-2">
                      <Coins className="w-3 h-3" />Repricing audit
                    </div>
                    {!repricing ? (
                      <button
                        data-testid="open-repricing-btn"
                        onClick={loadRepricing}
                        className="mono text-[10px] uppercase tracking-widest px-2 py-1 border border-industrial hover:border-primary-orange text-muted-industrial hover:text-primary-orange"
                      >
                        Show delivered dockets
                      </button>
                    ) : (
                      <button
                        onClick={() => setRepricing(null)}
                        className="mono text-[10px] uppercase tracking-widest px-2 py-1 border border-industrial hover:border-red-700 text-muted-industrial hover:text-red-400"
                      >
                        Hide
                      </button>
                    )}
                  </div>
                  {repricing && (
                    <div className="mt-2 space-y-1.5">
                      <div className="text-[10px] mono uppercase tracking-widest text-muted-industrial">
                        Current job rate: <span className="text-primary-orange">${repricing.current_rate.toFixed(2)}/m³</span> · {repricing.current_mix_code}
                      </div>
                      {repricing.dockets.length === 0 && (
                        <div className="text-[11px] text-muted-industrial">No dockets on this job yet.</div>
                      )}
                      <div className="max-h-[220px] overflow-y-auto space-y-1">
                        {repricing.dockets.map((d) => {
                          const selected = repricing.selected.has(d.docket_id);
                          const flag = Math.abs(d.delta) > 0.01;
                          return (
                            <label
                              key={d.docket_id}
                              data-testid={`reprice-row-${d.docket_no}`}
                              className={`flex items-center gap-2 border p-1.5 cursor-pointer text-[11px] ${selected ? "border-primary-orange bg-primary-orange/5" : "border-industrial hover:border-primary-orange/50"}`}
                            >
                              <input
                                type="checkbox"
                                checked={selected}
                                onChange={() => toggleRepriceRow(d.docket_id)}
                                className="w-3.5 h-3.5 accent-[rgb(var(--primary))]"
                              />
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-1.5">
                                  <span className="mono font-bold">{d.docket_no}</span>
                                  <span className="mono text-[10px] text-muted-industrial">{d.mix_code}</span>
                                  <span className="mono text-[10px] text-muted-industrial">·</span>
                                  <span className="mono text-[10px] text-muted-industrial">{d.quantity_m3}m³</span>
                                </div>
                                <div className="text-[10px] text-muted-industrial mono">
                                  {formatLocalDate(d.loaded_at)}
                                </div>
                                {(d.extras?.length > 0 || d.wait_charge > 0 || d.small_load_charge > 0) && (
                                  <div
                                    data-testid={`reprice-extras-${d.docket_no}`}
                                    className="flex flex-wrap gap-1 mt-1"
                                  >
                                    {d.extras?.map((x) => (
                                      <span
                                        key={x.id}
                                        title={`${x.name} · $${x.price_per_m3?.toFixed(2)}/m³`}
                                        className="mono text-[9px] uppercase tracking-wider px-1.5 py-0.5 border"
                                        style={{ borderColor: x.color || "#71717A", color: x.color || undefined }}
                                      >
                                        {x.name} +${x.amount.toFixed(2)}
                                      </span>
                                    ))}
                                    {d.wait_charge > 0 && (
                                      <span className="mono text-[9px] uppercase tracking-wider px-1.5 py-0.5 border border-industrial text-muted-industrial">
                                        WAIT +${d.wait_charge.toFixed(2)}
                                      </span>
                                    )}
                                    {d.small_load_charge > 0 && (
                                      <span className="mono text-[9px] uppercase tracking-wider px-1.5 py-0.5 border border-industrial text-muted-industrial">
                                        SMALL LOAD +${d.small_load_charge.toFixed(2)}
                                      </span>
                                    )}
                                  </div>
                                )}
                              </div>
                              <div className="text-right mono">
                                <div className="text-[11px]">
                                  ${d.billed_amount.toFixed(2)}
                                  {flag && <span className="text-primary-orange"> → ${d.proposed_amount.toFixed(2)}</span>}
                                </div>
                                <div className="text-[9px] text-muted-industrial">
                                  ${d.billed_rate.toFixed(2)}
                                  {flag && <span className="text-primary-orange"> → ${d.proposed_rate.toFixed(2)}</span>}
                                </div>
                                {(d.extras_total > 0 || d.wait_charge > 0 || d.small_load_charge > 0) && (
                                  <div className="text-[9px] text-muted-industrial mt-0.5">
                                    line ${d.line_total.toFixed(2)}
                                  </div>
                                )}
                              </div>
                              {flag && (
                                <span className={`mono text-[9px] font-bold ${d.delta > 0 ? "text-primary-orange" : "text-red-500"}`}>
                                  {d.delta > 0 ? "+" : ""}${d.delta.toFixed(2)}
                                </span>
                              )}
                              <div onClick={(e) => e.stopPropagation()}>
                                <QuickEditDocketPopover
                                  docket={{
                                    id: d.docket_id,
                                    docket_no: d.docket_no,
                                    quantity_m3: d.quantity_m3,
                                    price_per_m3: d.billed_rate,
                                    mix_code: d.mix_code,
                                    extra_tag_ids: (d.extras || []).map((x) => x.id),
                                    wait_charge: d.wait_charge || 0,
                                  }}
                                  mixes={mixes}
                                  extraTags={extraTags}
                                  testIdSuffix={d.docket_no}
                                  onSaved={loadRepricing}
                                />
                              </div>
                            </label>
                          );
                        })}
                      </div>
                      {repricing.dockets.length > 0 && (
                        <div className="flex items-center justify-between border-t border-industrial pt-2 mt-1">
                          <div className="text-[10px] mono uppercase tracking-widest text-muted-industrial">
                            Net change if all applied:
                            <span className={`ml-1.5 font-bold ${repricing.total_delta > 0 ? "text-primary-orange" : repricing.total_delta < 0 ? "text-red-500" : "text-muted-industrial"}`}>
                              {repricing.total_delta > 0 ? "+" : ""}${repricing.total_delta.toFixed(2)}
                            </span>
                          </div>
                          <button
                            data-testid="apply-repricing-btn"
                            onClick={applyRepricing}
                            disabled={repricing.selected.size === 0}
                            className="mono text-[10px] uppercase tracking-widest px-2.5 py-1.5 border border-primary-orange text-primary-orange bg-primary-orange/10 hover:bg-primary-orange/20 disabled:opacity-40 disabled:cursor-not-allowed"
                          >
                            Apply to {repricing.selected.size} docket{repricing.selected.size === 1 ? "" : "s"}
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
              {editCustomer && editCustomer.sites?.length > 0 && (
                <div>
                  <Label className="label-tag">Site</Label>
                  <Select value={editForm.site_id} onValueChange={(v) => setEditForm({ ...editForm, site_id: v })}>
                    <SelectTrigger data-testid="edit-site-select" className="rounded-none bg-transparent border-industrial"><SelectValue placeholder="Select site" /></SelectTrigger>
                    <SelectContent className="rounded-none bg-surface border-industrial">
                      {editCustomer.sites.map((s) => <SelectItem key={s.id} value={s.id}>{s.name} — {s.address}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label className="label-tag">Date</Label>
                  <Input data-testid="edit-job-date" type="date" value={editForm.date} onChange={(e) => setEditForm({ ...editForm, date: e.target.value })} className="rounded-none bg-transparent border-industrial mono" />
                </div>
                <div>
                  <Label className="label-tag">Qty (m³)</Label>
                  <Input data-testid="edit-job-qty" type="number" step="0.2" value={editForm.quantity_m3} onChange={(e) => setEditForm({ ...editForm, quantity_m3: parseFloat(e.target.value) })} className="rounded-none bg-transparent border-industrial mono" />
                </div>
                <div>
                  <Label className="label-tag">Time</Label>
                  <Input data-testid="edit-job-time" type="time" value={editForm.time_required} onChange={(e) => setEditForm({ ...editForm, time_required: e.target.value })} className="rounded-none bg-transparent border-industrial mono" />
                </div>
              </div>

              {/* v3.16 · Requested Mini Load — 10% surcharge on every docket */}
              <label
                className="flex items-start gap-2 cursor-pointer border border-industrial p-2 bg-surface-2/40 hover:border-primary-orange transition"
                data-testid="edit-mini-load-label"
              >
                <input
                  type="checkbox"
                  data-testid="edit-mini-load-checkbox"
                  checked={!!editForm.mini_load_surcharge}
                  onChange={(e) => setEditForm({ ...editForm, mini_load_surcharge: e.target.checked })}
                  className="mt-0.5"
                />
                <div className="flex-1">
                  <div className="mono text-[10px] uppercase tracking-widest text-ink">Requested Mini Load · <span className="text-primary-orange">+10%</span></div>
                  <div className="mono text-[9px] text-muted-industrial mt-0.5">
                    Any docket batched from this order after saving will carry a "Requested Mini Load Surcharge (10%)" line.
                  </div>
                </div>
              </label>

              {/* Ex-plant pickup — collected at the plant, no cartage/fuel surcharge */}
              <label
                className="flex items-start gap-2 cursor-pointer border border-industrial p-2 bg-surface-2/40 hover:border-primary-orange transition"
                data-testid="edit-ex-plant-label"
              >
                <input
                  type="checkbox"
                  data-testid="edit-ex-plant-checkbox"
                  checked={!!editForm.ex_plant}
                  onChange={(e) => setEditForm({ ...editForm, ex_plant: e.target.checked })}
                  className="mt-0.5"
                />
                <div className="flex-1">
                  <div className="mono text-[10px] uppercase tracking-widest text-ink">Ex-plant · <span className="text-primary-orange">picked up at plant</span></div>
                  <div className="mono text-[9px] text-muted-industrial mt-0.5">
                    Customer collects at the plant — removes all cartage/delivery and the fuel surcharge. Prints "EX-PLANT" on the docket.
                  </div>
                </div>
              </label>

              {/* All-up price BEFORE delivery — read it straight off the screen on the phone. */}
              <OrderQuote
                form={editForm}
                customerName={customers.find((c) => c.id === editForm.customer_id)?.name || editJob.customer_name}
                sitePhone={editForm.site_contact_phone}
                plantId={plantId}
                onRate={(mixId, rate) => setEditForm((f) => ({ ...f, lines: (f.lines || []).map((l) => (l.mix_id === mixId ? { ...l, price_per_m3: rate } : l)) }))}
              />

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="label-tag">PO Number</Label>
                  <Input data-testid="edit-job-po" value={editForm.po_number} onChange={(e) => setEditForm({ ...editForm, po_number: e.target.value })} className="rounded-none bg-transparent border-industrial mono" />
                </div>
                <div>
                  <Label className="label-tag">Ordered By</Label>
                  <Input data-testid="edit-job-ordered-by" placeholder="Person on site" value={editForm.ordered_by || ""} onChange={(e) => setEditForm({ ...editForm, ordered_by: e.target.value })} className="rounded-none bg-transparent border-industrial mono" />
                </div>
                <label className="col-span-2 flex items-center gap-2 cursor-pointer select-none" data-testid="edit-job-price-on-docket">
                  <input type="checkbox" checked={!!editForm.price_on_docket} onChange={(e) => setEditForm({ ...editForm, price_on_docket: e.target.checked })} className="accent-[#F97316] w-4 h-4" />
                  <span className="mono text-[10px] uppercase tracking-widest text-ink inline-flex items-center gap-1"><Receipt className="w-3 h-3" /> Show price on docket</span>
                </label>
              </div>
              <div>
                <Label className="label-tag">Site contact mobile <span className="text-muted-industrial normal-case font-normal">· gets the dispatch text</span></Label>
                <Input data-testid="edit-job-site-contact-phone" type="tel" inputMode="tel" placeholder="04xx xxx xxx" value={editForm.site_contact_phone || ""} onChange={(e) => setEditForm({ ...editForm, site_contact_phone: e.target.value })} className="rounded-none bg-transparent border-industrial mono" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="label-tag">Trucks needed</Label>
                  <Input
                    data-testid="edit-job-trucks-needed"
                    type="number"
                    min="1"
                    step="1"
                    value={editForm.trucks_needed ?? ""}
                    onChange={(e) => setEditForm({ ...editForm, trucks_needed: e.target.value })}
                    placeholder="e.g. 5"
                    className="rounded-none bg-transparent border-industrial mono"
                  />
                </div>
                <div>
                  <Label className="label-tag">Load spacing (min)</Label>
                  <Input
                    data-testid="edit-job-load-spacing"
                    type="number"
                    min="0"
                    step="5"
                    value={editForm.load_spacing_min ?? ""}
                    onChange={(e) => setEditForm({ ...editForm, load_spacing_min: e.target.value })}
                    placeholder="e.g. 20"
                    className="rounded-none bg-transparent border-industrial mono"
                  />
                </div>
              </div>
              <div>
                <Label className="label-tag">Pump Company</Label>
                <select
                  data-testid="edit-job-pump-company"
                  value={editForm.pump_company_id ?? ""}
                  onChange={(e) => setEditForm({ ...editForm, pump_company_id: e.target.value, pump_unit_id: "" })}
                  className="w-full mt-1 rounded-none bg-transparent border border-industrial mono text-sm p-2"
                >
                  <option value="">— No pump / owner-placed —</option>
                  {pumpCompanies.map((pc) => (
                    <option key={pc.id} value={pc.id}>{pc.name}{pc.phone ? ` · ` : ""}{pc.partner_id ? "" : " · not on partner network"}</option>
                  ))}
                </select>
                <PumpUnitSelect companyId={editForm.pump_company_id} value={editForm.pump_unit_id}
                  onChange={(v) => setEditForm({ ...editForm, pump_unit_id: v })} testId="edit-job-pump-unit" />
              </div>
              {editForm.time_required && editForm.load_spacing_min && editForm.trucks_needed && (
                <div className="mono text-[10px] uppercase tracking-widest text-primary-orange border border-primary-orange/40 bg-primary-orange/5 px-2 py-1.5">
                  Schedule: {computeLoadSlots(editForm.time_required, parseInt(editForm.trucks_needed, 10), parseInt(editForm.load_spacing_min, 10)).join(" · ")}
                </div>
              )}
              <div>
                <Label className="label-tag">Distance from plant (km)</Label>
                <Input
                  data-testid="edit-job-distance"
                  type="number"
                  step="0.1"
                  min="0"
                  value={editForm.delivery_km ?? ""}
                  onChange={(e) => setEditForm({ ...editForm, delivery_km: e.target.value === "" ? "" : parseFloat(e.target.value) })}
                  placeholder="e.g. 30 → $10 fee"
                  className="rounded-none bg-transparent border-industrial mono"
                />
                {editForm.delivery_km !== "" && editForm.delivery_km != null && !isNaN(Number(editForm.delivery_km)) && (() => {
                  const km = Number(editForm.delivery_km);
                  const over = Math.max(0, km - 25);
                  const fee = over * 2;
                  return (
                    <div className={`mt-1.5 mono text-[10px] uppercase tracking-widest ${over > 0 ? "text-primary-orange" : "text-emerald-500"}`}>
                      {over > 0
                        ? `+${over.toFixed(1)}km over · Delivery fee $${fee.toFixed(2)}`
                        : `Within 25km zone · no delivery fee`}
                    </div>
                  );
                })()}
              </div>
              <div>
                <Label className="label-tag flex items-center gap-1.5" style={{ color: "#FBBF24" }}>
                  <Sparkles className="w-3 h-3" /> Extras
                </Label>
                <ExtraTagPicker tags={extraTags} selectedIds={editForm.extra_tag_ids} onChange={(ids) => setEditForm({ ...editForm, extra_tag_ids: ids })} testid="edit-job-extra-tags" />
                <div className="label-tag mt-2">One-off surcharges</div>
                <SurchargePicker items={surcharges} selectedIds={editForm.surcharge_ids} onChange={(ids) => setEditForm({ ...editForm, surcharge_ids: ids })} testid="edit-job-surcharges" />
                {oohSurcharge && isOutOfHours(editForm.date, editForm.time_required) && !(editForm.surcharge_ids || []).includes(oohSurcharge.id) && (
                  <div className="mono text-[10px] uppercase tracking-widest mt-1.5 px-2 py-1 border border-red-500 text-red-500" data-testid="edit-ooh-fee-missing">
                    Out of hours pour · ${Number(oohSurcharge.amount).toLocaleString()} fee not applied
                  </div>
                )}
                <Input data-testid="edit-job-extras" value={editForm.extras} onChange={(e) => setEditForm({ ...editForm, extras: e.target.value })} placeholder="Additional notes (optional)" className="rounded-none bg-transparent border-industrial mono mt-2 h-8 text-xs" />
                <div className="label-tag mt-3 flex items-center justify-between">
                  <span>Notes / site instructions</span>
                  <label className="flex items-center gap-1.5 cursor-pointer normal-case">
                    <input
                      type="checkbox"
                      data-testid="edit-job-notes-to-driver"
                      checked={editForm.notes_to_driver !== false}
                      onChange={(e) => setEditForm({ ...editForm, notes_to_driver: e.target.checked })}
                      className="w-3.5 h-3.5 accent-[rgb(var(--primary))]"
                    />
                    <span className="mono text-[10px] uppercase tracking-widest text-primary-orange">Send to driver</span>
                  </label>
                </div>
                <textarea
                  data-testid="edit-job-notes"
                  value={editForm.notes || ""}
                  onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
                  rows={3}
                  placeholder="e.g. Gate code 4821 · park on the verge · ask for Bevan"
                  className="w-full rounded-none bg-transparent border border-industrial mono text-xs p-2"
                />
              </div>
            </div>
          )}
          </div>
          <DialogFooter className="px-6 py-3 border-t-2 border-primary-orange bg-surface-2 shrink-0 sticky bottom-0">
            <Button data-testid="edit-job-save" onClick={saveEdit} className="rounded-none bg-primary-orange hover:bg-[rgb(var(--primary-hover))] uppercase tracking-widest text-xs font-bold w-full">Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Swap-truck picker — replace one assigned truck with another,
          delivered_m3 stays intact, any active batch/docket follows the swap. */}
      <Dialog open={!!swapCtx} onOpenChange={(v) => { if (!v) setSwapCtx(null); }}>
        <DialogContent className="rounded-none bg-surface border-industrial max-w-md" data-testid="swap-truck-dialog">
          <DialogHeader>
            <DialogTitle className="heading uppercase tracking-tight flex items-center gap-2">
              <RefreshCcw className="w-4 h-4 text-primary-orange" /> Swap Truck
            </DialogTitle>
          </DialogHeader>
          {swapCtx && (() => {
            const usedIds = new Set(swapCtx.job.assigned_truck_ids || []);
            const eligible = trucks
              .filter((t) => !usedIds.has(t.id))
              .filter((t) => t.status !== "out_of_service")
              .filter((t) => !swapCtx.job.plant_id || !t.plant_id || t.plant_id === swapCtx.job.plant_id)
              .filter((t) => !t.current_job_id || t.current_job_id === swapCtx.job.id);
            return (
              <div className="space-y-3">
                <div className="border border-industrial p-3 bg-surface-2/40 space-y-1">
                  <div className="mono text-[10px] uppercase tracking-widest text-muted-industrial">Replacing on {swapCtx.job.customer_name}</div>
                  <div className="mono text-sm font-bold text-ink">{swapCtx.fromTruck.number} <span className="text-muted-industrial">→ ?</span></div>
                  <div className="mono text-[10px] text-muted-industrial">
                    delivered {swapCtx.job.delivered_m3 || 0}m³ / ordered {swapCtx.job.quantity_m3}m³ · totals stay the same
                  </div>
                </div>
                <div className="label-tag">Pick replacement</div>
                {eligible.length === 0 ? (
                  <div className="mono text-xs text-muted-industrial border border-dashed border-industrial p-6 text-center">
                    No available trucks to swap in. Return one to yard first.
                  </div>
                ) : (
                  <div className="max-h-[300px] overflow-y-auto space-y-1.5">
                    {eligible.map((t) => (
                      <button
                        key={t.id}
                        data-testid={`swap-pick-${t.number}`}
                        onClick={() => doSwap(t.id)}
                        className="w-full text-left border border-industrial hover:border-primary-orange px-3 py-2 flex items-center justify-between gap-2 group"
                      >
                        <div>
                          <div className="mono text-sm font-bold text-ink group-hover:text-primary-orange">{t.number}</div>
                          <div className="text-[10px] text-muted-industrial mono">
                            {t.driver_name || "No driver"} · {t.capacity_m3}m³ · {(t.status || "?").toUpperCase()}
                          </div>
                        </div>
                        <RefreshCcw className="w-3.5 h-3.5 text-muted-industrial group-hover:text-primary-orange" />
                      </button>
                    ))}
                  </div>
                )}
                <div className="text-[10px] mono text-muted-industrial uppercase tracking-widest">
                  m³ totals, dockets and batches transfer to the new truck.
                </div>
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* v3.7 · Pre-start safety warning before assign */}
      <Dialog open={!!assignWarn} onOpenChange={(o) => !o && setAssignWarn(null)}>
        <DialogContent className="rounded-none bg-surface border-2 border-red-600 max-w-md" data-testid="prestart-warn-dialog">
          <DialogHeader>
            <DialogTitle className="heading uppercase tracking-wider text-red-400 flex items-center gap-2">
              <span className="w-2 h-2 bg-red-500 animate-pulse inline-block" /> STOP · Pre-Start Not Completed
            </DialogTitle>
          </DialogHeader>
          {assignWarn && (
            <div className="space-y-3">
              <div className="text-sm text-ink">
                Truck <span className="mono font-bold text-primary-orange">{assignWarn.truck.number}</span> has not
                submitted its pre-start checklist for today. Proceed with caution?
              </div>
              {(() => {
                const ps = preStartByTruck[assignWarn.truck.id];
                if (!ps) return null;
                if (!ps.submitted) return <div className="mono text-[11px] text-red-300">No pre-start submission today.</div>;
                if (ps.overall === "fail") return <div className="mono text-[11px] text-red-300">Latest pre-start reported a fault (still unresolved).</div>;
                return null;
              })()}
              <div className="flex flex-col sm:flex-row gap-2 pt-2">
                <Button
                  variant="outline"
                  onClick={() => setAssignWarn(null)}
                  data-testid="prestart-warn-cancel"
                  className="rounded-none flex-1 border-industrial bg-transparent uppercase text-xs tracking-widest"
                >
                  Cancel
                </Button>
                <Button
                  variant="outline"
                  onClick={() => { setAssignWarn(null); nav(`/fleet-health`); }}
                  data-testid="prestart-warn-history"
                  className="rounded-none flex-1 border-industrial bg-transparent uppercase text-xs tracking-widest text-primary-orange"
                >
                  View Fleet Health
                </Button>
                <Button
                  onClick={async () => {
                    if (assignWarn) {
                      const { truck, job } = assignWarn;
                      setOverride((o) => ({ ...o, [truck.id]: true }));
                      setAssignWarn(null);
                      await doAssign(job, truck);
                    }
                  }}
                  data-testid="prestart-warn-override"
                  className="rounded-none flex-1 bg-red-600 hover:bg-red-500 text-white uppercase text-xs tracking-widest font-bold"
                >
                  Override & Assign
                </Button>
              </div>
              <div className="mono text-[10px] uppercase tracking-widest text-muted-industrial">
                Override applies for this session only. The truck will still flash red until a pre-start is submitted.
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* v3.16 · Truck-load volume prompt · fires the instant a truck is
          assigned so the dispatcher can dial in a part-load (e.g. 3.2 m³ in
          a 6 m³ truck). Skipping keeps the default = truck capacity so the
          in-transit commitment still reads high. */}
      <Dialog open={!!loadPrompt} onOpenChange={(o) => !o && setLoadPrompt(null)}>
        <DialogContent
          data-testid="load-prompt-dialog"
          className="rounded-none bg-surface border-industrial max-w-sm"
        >
          <DialogHeader>
            <DialogTitle className="heading uppercase tracking-wider text-primary-orange flex items-center gap-2">
              <Truck className="w-4 h-4" /> Truck Load
            </DialogTitle>
          </DialogHeader>
          {loadPrompt && (() => {
            const cap = Number(loadPrompt.truck.capacity_m3 || 0);
            const parsed = parseFloat(loadPrompt.value);
            const valid = Number.isFinite(parsed) && parsed > 0;
            const overCap = valid && parsed > cap + 0.001;
            const remaining = Math.max(
              0,
              Number(loadPrompt.job.quantity_m3 || 0) - Number(loadPrompt.job.delivered_m3 || 0)
            );
            const preset = (val) => setLoadPrompt((p) => p && { ...p, value: String(val) });
            return (
              <div className="space-y-3">
                <div className="text-sm mono">
                  <span className="text-muted-industrial">Truck</span>{" "}
                  <span className="font-bold text-primary-orange">{loadPrompt.truck.number}</span>{" "}
                  <span className="text-muted-industrial">on</span>{" "}
                  <span className="font-bold">{loadPrompt.job.customer_name}</span>
                </div>
                <div className="text-xs mono text-muted-industrial">
                  Capacity <span className="text-ink">{cap} m³</span> · Job still needs{" "}
                  <span className="text-ink">{remaining.toFixed(1)} m³</span>
                </div>
                <div>
                  <Label className="label-tag">Load size (m³)</Label>
                  <Input
                    data-testid="load-prompt-input"
                    type="number"
                    step="0.1"
                    autoFocus
                    value={loadPrompt.value}
                    onChange={(e) => setLoadPrompt({ ...loadPrompt, value: e.target.value })}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && valid && !overCap) submitLoadPrompt();
                    }}
                    className="rounded-none bg-transparent border-industrial mono text-lg"
                  />
                  {overCap && (
                    <div className="mono text-[10px] text-red-500 mt-1">
                      Exceeds truck capacity ({cap} m³)
                    </div>
                  )}
                </div>
                <div className="flex gap-1 flex-wrap">
                  {[cap, cap / 2, Math.min(cap, remaining || cap)]
                    .filter((v, i, arr) => v > 0 && arr.indexOf(v) === i)
                    .map((v, idx) => (
                      <button
                        key={idx}
                        type="button"
                        data-testid={`load-preset-${idx}`}
                        onClick={() => preset(Number(v.toFixed(1)))}
                        className="mono text-[10px] uppercase tracking-widest px-2 py-1 border border-industrial text-muted-industrial hover:border-primary-orange hover:text-primary-orange"
                      >
                        {idx === 0 ? "FULL" : idx === 1 ? "HALF" : "FIT REMAINING"} · {v.toFixed(1)}
                      </button>
                    ))}
                </div>
                <div className="flex gap-2 pt-1">
                  <Button
                    variant="outline"
                    data-testid="load-prompt-skip"
                    onClick={() => setLoadPrompt(null)}
                    className="rounded-none flex-1 border-industrial uppercase tracking-widest text-xs"
                  >
                    Skip (keep {cap} m³)
                  </Button>
                  <Button
                    data-testid="load-prompt-save"
                    onClick={submitLoadPrompt}
                    disabled={!valid || overCap || loadPromptSaving}
                    className="rounded-none flex-1 bg-primary-orange hover:bg-[rgb(var(--primary-hover))] text-black uppercase tracking-widest text-xs font-bold disabled:opacity-40"
                  >
                    {loadPromptSaving ? "Saving…" : "Set Load"}
                  </Button>
                </div>
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>
    </div>
  );
}
